import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlacesAutocomplete } from '@/components/ui/PlacesAutocomplete';
import EngineRating from '@/components/mfcwl-report/EngineRating';
import SoundTypeAnalysis from '@/components/mfcwl-report/SoundTypeAnalysis';
import SoundHistogram from '@/components/mfcwl-report/SoundHistogram';
import WisedriveAnalysis from '@/components/mfcwl-report/WisedriveAnalysis';
import LiveProgressServiceHistoryTab from '@/components/LiveProgressModal/ServiceHistoryTab';
import ScanHistory from '@/components/LiveProgressModal/ScanHistory';
import { toast } from 'sonner';
import {
  Briefcase, Upload, Mic, FileText, Code, Loader2, Eye, Download,
  UserPlus, CheckCircle2, AlertTriangle, History, Cpu, Volume2,
  RefreshCw, MapPin, Globe, FileDown, Plus, Search, Edit3,
  Bluetooth, Sparkles, Zap, ChevronDown, CircleDot, Gauge, RotateCcw,
  CheckCircle, Wrench, Activity, Thermometer, Fuel, Wind, Timer, Play, FileUp,
  Settings as SettingsIcon, Copy, MessageSquare, Webhook, Bell, Send,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BASE = `${API_URL}/b2b/mfcwl/admin`;
const ADMIN_ROLES = new Set(['CEO', 'CTO', 'ADMIN', 'SUPER_ADMIN']);

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const fmtDateOnly = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtTimeOnly = (iso) => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
const stripSpaces = (s) => (s || '').replace(/\s+/g, '').toUpperCase();

// Collapse Surepass/MFCWL verbose vehicle names → "Make Model Year"
// "Ford India Pvt Ltd" → "Ford"; "3.2 Endeavour 4 4 At Titanium" → "Endeavour"
function compactMakeModel(v) {
  if (!v) return '';
  const stopWords = /\b(india|pvt|private|ltd|limited|motors?|company|corp|corporation|automobiles?|suzuki|group)\b/gi;
  const make = String(v.make || '')
    .replace(stopWords, '')
    .replace(/\s+/g, ' ').trim()
    .split(' ')[0] || '';
  // Model: take first alphabetic word (drop "3.2", "4 4 At Titanium", etc.)
  const model = String(v.model || '')
    .split(/\s+/)
    .find(tok => /^[A-Za-z][A-Za-z-]{1,}$/.test(tok)) || '';
  return [make, model, v.year].filter(Boolean).join(' ').trim();
}
const STATUS_OPTIONS = ['NEW', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];
const ANALYSIS_STATUS_LABEL = {
  AWAITING_VIDEO: 'Awaiting video',
  QUEUED: 'Queued',
  MIRRORING_OBD: 'Syncing OBD',
  ANALYZING: 'Analyzing…',
  COMPLETED: 'Ready',
  FAILED: 'Failed',
};
const ANALYSIS_STATUS_COLOR = {
  AWAITING_VIDEO: 'bg-gray-100 text-gray-500',
  QUEUED:         'bg-sky-100 text-sky-700',
  MIRRORING_OBD:  'bg-indigo-100 text-indigo-700',
  ANALYZING:      'bg-indigo-100 text-indigo-700',
  COMPLETED:      'bg-emerald-100 text-emerald-700',
  FAILED:         'bg-rose-100 text-rose-700',
};
const STATUS_COLOR = {
  NEW:        'bg-blue-100 text-blue-700',
  ASSIGNED:   'bg-amber-100 text-amber-700',
  IN_PROGRESS:'bg-indigo-100 text-indigo-700',
  COMPLETED:  'bg-emerald-100 text-emerald-700',
  CANCELLED:  'bg-gray-100 text-gray-500',
};

// ── WDS-ID badge — copy-on-click. Keep small and tight. ────────────
function WdsIdBadge({ wisedriveId, size = 'xs' }) {
  if (!wisedriveId) return null;
  const label = String(wisedriveId).toUpperCase();
  const copy = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      navigator.clipboard.writeText(wisedriveId);
      toast.success('Copied to clipboard');
    } catch { /* ignore */ }
  };
  return (
    <button
      onClick={copy}
      className={[
        'inline-flex items-center gap-1 font-mono font-medium',
        'bg-indigo-50 text-indigo-700 border border-indigo-200',
        'hover:bg-indigo-100 rounded-md px-1.5 py-0.5',
        size === 'sm' ? 'text-[11px]' : 'text-[10px]',
      ].join(' ')}
      title={`Wisedrive Reference ID — ${wisedriveId} (click to copy)`}
      data-testid={`wds-id-badge-${wisedriveId}`}
    >
      <span>{label}</span>
      <Copy className="h-2.5 w-2.5 opacity-60" />
    </button>
  );
}

export default function MfcwlDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('manpower');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const code = (user?.role_code || user?.role || '').toUpperCase();
  const roles = new Set((user?.roles || []).map(r => (r.code || '').toUpperCase()));
  const isAdmin = ADMIN_ROLES.has(code) || [...roles].some(r => ADMIN_ROLES.has(r));

  if (!user) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="p-12 text-center" data-testid="mfcwl-access-denied">
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-3 text-lg font-semibold text-gray-700">Admin access required</h2>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid="mfcwl-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-indigo-600" />
            <h1 className="text-2xl font-semibold tracking-tight">MFCWL</h1>
            <Badge variant="outline" className="ml-2">Mahindra First Choice Wheels · B2B</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Partner inspection queue, engine sound AI analyses, service history, reports & invoices.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${BASE}/api-docs.pdf`, { responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      const a = document.createElement('a');
                      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                      a.href = url;
                      a.download = `MFCWL_WiseDrive_API_Guide_v1.0_${today}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('API docs PDF downloaded');
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Download failed');
                    }
                  }}
                  data-testid="download-api-docs-toolbar"
                  className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
            <Download className="h-3.5 w-3.5 mr-1.5" /> API Docs (PDF)
          </Button>
          <Button variant="outline" size="sm"
                  onClick={() => setSettingsOpen(true)}
                  data-testid="mfcwl-settings-btn">
            <SettingsIcon className="h-3.5 w-3.5 mr-1.5" /> MFCWL Settings
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <TabsList data-testid="mfcwl-tabs">
            <TabsTrigger value="manpower" data-testid="mfcwl-tab-manpower">
              <Cpu className="h-3.5 w-3.5 mr-1.5" /> Manpower + Tech
            </TabsTrigger>
            <TabsTrigger value="ai" data-testid="mfcwl-tab-ai">
              <Volume2 className="h-3.5 w-3.5 mr-1.5" /> AI Analysis
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="mfcwl-tab-invoices">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Invoices
            </TabsTrigger>
          </TabsList>
          {tab === 'manpower' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm"
                      onClick={() => document.dispatchEvent(new CustomEvent('mfcwl:refresh'))}
                      data-testid="manpower-refresh">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
              </Button>
              <Button size="sm"
                      onClick={() => document.dispatchEvent(new CustomEvent('mfcwl:new-inspection'))}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="new-inspection-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> New Inspection
              </Button>
            </div>
          )}
        </div>

        <TabsContent value="manpower"><ManpowerTab /></TabsContent>
        <TabsContent value="ai"><AIAnalysisTab /></TabsContent>
        <TabsContent value="invoices"><InvoicesTab /></TabsContent>
      </Tabs>

      <MfcwlSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB 1 — Manpower + Tech  (inspections requiring our mechanic)
// ═══════════════════════════════════════════════════════════════════
function ManpowerTab() {
  const [rows, setRows] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [editInspection, setEditInspection] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [insp, mech] = await Promise.all([
        axios.get(`${BASE}/inspections`),
        axios.get(`${BASE}/mechanics`),
      ]);
      setRows(insp.data.inspections || []);
      setMechanics(mech.data.mechanics || []);
    } catch { toast.error('Failed to load MFCWL inspections'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Listen to header-level Refresh / + New Inspection buttons (lifted out
  // of this tab and into the MFCWL page header for vertical compactness).
  useEffect(() => {
    const onRefresh = () => load();
    const onNew = () => setNewOpen(true);
    document.addEventListener('mfcwl:refresh', onRefresh);
    document.addEventListener('mfcwl:new-inspection', onNew);
    return () => {
      document.removeEventListener('mfcwl:refresh', onRefresh);
      document.removeEventListener('mfcwl:new-inspection', onNew);
    };
  }, [load]);

  // ── Client-side search across MFCWL ID, WDS ID, customer name/phone,
  //    vehicle reg/make/model, city, and status. Multi-token: every
  //    space-separated token must match somewhere in the haystack
  //    (Google-style AND search). Empty query → all rows passed through.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    const tokens = q.split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
      const v = r.vehicle || {};
      const haystack = [
        r.inspection_id,
        r.wisedrive_id,
        r.customer?.name,
        r.customer?.phone,
        r.status,
        r.analysis_status,
        v.reg_no, v.make, v.model, v.year, v.fuel_type, v.transmission,
        r.location?.city, r.location?.address, r.location?.pincode,
        r.mechanic?.name, r.mechanic?.phone,
        r.service_tier,
      ]
        .filter(Boolean)
        .map((x) => String(x).toLowerCase())
        .join(' | ');
      return tokens.every((t) => haystack.includes(t));
    });
  }, [rows, search]);

  return (
    <div className="mt-4 space-y-3">
      {/* Search bar — filters the inspection list across MFCWL ID, WDS ID,
          customer, vehicle, city, status, mechanic, service tier. */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by MFCWL ID, WDS, name, phone, reg no, city…"
            className="pl-9 pr-8 h-9"
            data-testid="mfcwl-inspections-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xs font-medium px-1"
              data-testid="mfcwl-inspections-search-clear"
              aria-label="Clear search"
            >✕</button>
          )}
        </div>
        {!loading && (
          <span
            className="text-xs text-gray-500"
            data-testid="mfcwl-inspections-search-count"
          >
            {search
              ? `${filteredRows.length} of ${rows.length}`
              : `${rows.length} inspection${rows.length === 1 ? '' : 's'}`}
          </span>
        )}
      </div>
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-x-auto" data-testid="mfcwl-inspections-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Date / Time</th>
                <th className="text-left px-3 py-2">MFCWL ID</th>
                <th className="text-left px-3 py-2">WDS Tracking</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">City</th>
                <th className="text-left px-3 py-2">Vehicle</th>
                <th className="text-left px-3 py-2">Assign to</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2 w-[140px]">Report</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-gray-400" data-testid="mfcwl-inspections-empty">
                  {search ? `No inspections match “${search}”` : 'No inspections yet'}
                </td></tr>
              ) : filteredRows.map((r) => (
                <Row key={r.wisedrive_id} r={r} mechanics={mechanics}
                     onOpen={() => setOpenId(r.inspection_id)}
                     onEdit={() => setEditInspection(r)}
                     onRefresh={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InspectionDrawer inspectionId={openId} onClose={() => setOpenId(null)} onRefresh={load} />
      <NewInspectionModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={(id) => {
        setNewOpen(false);
        load();
        setOpenId(id);
      }} />
      <NewInspectionModal
        open={!!editInspection}
        mode="edit"
        initialInspection={editInspection}
        onClose={() => setEditInspection(null)}
        onCreated={() => { setEditInspection(null); load(); }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// + New Inspection — Surepass RC lookup + Google Places + schedule
// ═══════════════════════════════════════════════════════════════════
const TRANSMISSIONS = ['Manual', 'Automatic', 'AMT', 'CVT', 'DCT'];
const FUELS = ['Petrol', 'Diesel', 'CNG', 'Hybrid', 'Electric', 'LPG'];

function initialForm() {
  return {
    customer_name: '', customer_phone: '',
    location_address: '', location_city: '', location_pincode: '',
    location_lat: null, location_lng: null,
    scheduled_at: '',
    vehicle_make: '', vehicle_model: '', vehicle_year: '',
    vehicle_reg_no: '',
    transmission: '', fuel_type: '',
    km_driven: '', has_turbo: false,
    service_tier: 'manpower_tech',
    notes: '',
    assigned_mechanic_id: '',   // optional — assign on save
  };
}

function NewInspectionModal({ open, onClose, onCreated, mode = 'create', initialInspection = null }) {
  const isEdit = mode === 'edit';
  const [rc, setRc] = useState('');
  const [rcLoading, setRcLoading] = useState(false);
  const [rcFetched, setRcFetched] = useState(false);
  const [mechanics, setMechanics] = useState([]);
  const [form, setForm] = useState(initialForm());
  const [submitting, setSubmitting] = useState(false);

  // Hydrate from initialInspection when opening in edit mode
  useEffect(() => {
    if (!open) return;
    if (isEdit && initialInspection) {
      const insp = initialInspection;
      const v = insp.vehicle || {};
      const loc = insp.location || {};
      const cust = insp.customer || {};
      // datetime-local expects "YYYY-MM-DDTHH:mm" in LOCAL time
      let sched = '';
      if (insp.scheduled_at) {
        const d = new Date(insp.scheduled_at);
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        sched = local.toISOString().slice(0, 16);
      }
      setForm({
        customer_name: cust.name || '', customer_phone: cust.phone || '',
        location_address: loc.address || '', location_city: loc.city || '',
        location_pincode: loc.pincode || '',
        location_lat: loc.lat ?? null, location_lng: loc.lng ?? null,
        scheduled_at: sched,
        vehicle_make: v.make || '', vehicle_model: v.model || '',
        vehicle_year: v.year || '', vehicle_reg_no: v.reg_no || '',
        transmission: v.transmission || '', fuel_type: v.fuel_type || '',
        km_driven: v.km_driven ?? '', has_turbo: !!v.has_turbo,
        service_tier: insp.service_tier || 'manpower_tech',
        notes: insp.notes || '',
        assigned_mechanic_id: insp.mechanic?.id || '',
      });
      setRc(v.reg_no || '');
      setRcFetched(true);   // jump straight to full form
    } else if (!isEdit) {
      setForm(initialForm());
      setRc(''); setRcFetched(false);
    }
  }, [open, isEdit, initialInspection]);

  // Load mechanics once the modal opens (filtered client-side by city)
  useEffect(() => {
    if (!open) return;
    axios.get(`${BASE}/mechanics`)
      .then(r => setMechanics(r.data.mechanics || []))
      .catch(() => {});
  }, [open]);

  // Pre-fill scheduled_at with now + 30 min for CREATE only
  useEffect(() => {
    if (open && !isEdit && !form.scheduled_at) {
      const d = new Date(Date.now() + 30 * 60 * 1000);
      d.setSeconds(0, 0);
      d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
      const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString().slice(0, 16);
      setForm(f => ({ ...f, scheduled_at: iso }));
    }
  }, [open, isEdit, form.scheduled_at]);

  const reset = () => {
    setRc(''); setRcFetched(false); setRcLoading(false); setSubmitting(false);
    setForm(initialForm());
  };

  const lookupRc = async () => {
    const clean = rc.replace(/\s+/g, '').toUpperCase();
    if (clean.length < 6) { toast.error('Enter a valid RC number'); return; }
    setRcLoading(true);
    try {
      const r = await axios.get(`${BASE}/rc-lookup/${clean}`);
      const d = r.data;
      setForm(f => ({
        ...f,
        vehicle_reg_no: d.rc_number || clean,
        vehicle_make: d.make || '',
        vehicle_model: d.model || '',
        vehicle_year: d.year || '',
        fuel_type: d.fuel_type || '',
        customer_name: f.customer_name || d.owner_name || '',
      }));
      setRcFetched(true);
      toast.success('Vehicle details fetched');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'RC lookup failed — enter manually');
      setRcFetched(true);  // still let them fill manually
      setForm(f => ({ ...f, vehicle_reg_no: clean }));
    } finally { setRcLoading(false); }
  };

  const onPlaceSelect = (place) => {
    setForm(f => ({
      ...f,
      location_address: place.address || '',
      location_city:    place.city || '',     // always overwrite
      location_lat:     place.latitude ?? null,
      location_lng:     place.longitude ?? null,
      // New city → clear current mechanic selection (city filter may change)
      assigned_mechanic_id: place.city && place.city !== f.location_city ? '' : f.assigned_mechanic_id,
    }));
  };

  const submit = async () => {
    // Required field validation
    const missing = [];
    if (!form.customer_name) missing.push('Customer name');
    if (!form.vehicle_make || !form.vehicle_model || !form.vehicle_year) missing.push('Vehicle details');
    if (!form.transmission) missing.push('Transmission');
    if (!form.fuel_type) missing.push('Fuel type');
    if (!form.km_driven || Number(form.km_driven) < 0) missing.push('Kilometres driven');
    if (!form.location_address) missing.push('Address');
    if (!form.scheduled_at) missing.push('Schedule date/time');
    if (missing.length) { toast.error(`Fill: ${missing.join(', ')}`); return; }

    setSubmitting(true);
    try {
      if (isEdit) {
        // PATCH payload — only send changed fields (backend accepts partial)
        const payload = {
          customer_name:    form.customer_name,
          customer_phone:   form.customer_phone || null,
          location_address: form.location_address,
          location_city:    form.location_city || null,
          location_pincode: form.location_pincode || null,
          location_lat:     form.location_lat,
          location_lng:     form.location_lng,
          scheduled_at:     new Date(form.scheduled_at).toISOString(),
          vehicle_make:     form.vehicle_make,
          vehicle_model:    form.vehicle_model,
          vehicle_year:     Number(form.vehicle_year),
          vehicle_reg_no:   stripSpaces(form.vehicle_reg_no),
          transmission:     form.transmission,
          fuel_type:        form.fuel_type,
          km_driven:        Number(form.km_driven),
          has_turbo:        !!form.has_turbo,
          service_tier:     form.service_tier,
          notes:            form.notes || null,
        };
        const id = initialInspection.inspection_id;
        const r = await axios.patch(`${BASE}/inspections/${id}`, payload);
        // Re-assign mechanic if it changed
        const newMech = form.assigned_mechanic_id;
        const oldMech = initialInspection.mechanic?.id || '';
        if (newMech && newMech !== oldMech) {
          try {
            await axios.post(`${BASE}/inspections/${id}/assign`,
                             { mechanic_id: newMech });
          } catch {
            toast.warning('Inspection updated but mechanic assign failed');
          }
        }
        toast.success(r.data.changed?.length
          ? `Updated · ${r.data.changed.length} field${r.data.changed.length !== 1 ? 's' : ''}`
          : 'Inspection updated');
        onCreated?.(id);
        return;
      }

      // Server now generates the inspection_id (MFCWL-DDMMYY-XXXXXX);
      // we read it back from the create response.
      const payload = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone || null,
        location_address: form.location_address,
        location_city:    form.location_city || null,
        location_pincode: form.location_pincode || null,
        location_lat:     form.location_lat,
        location_lng:     form.location_lng,
        scheduled_at:     new Date(form.scheduled_at).toISOString(),
        vehicle_make:     form.vehicle_make,
        vehicle_model:    form.vehicle_model,
        vehicle_year:     Number(form.vehicle_year),
        vehicle_reg_no:   stripSpaces(form.vehicle_reg_no),
        transmission:     form.transmission,
        fuel_type:        form.fuel_type,
        km_driven:        Number(form.km_driven),
        has_turbo:        !!form.has_turbo,
        service_tier:     form.service_tier,
        notes:            form.notes || null,
      };
      const r = await axios.post(`${BASE}/inspections`, payload);
      const inspection_id = r.data.inspection_id;
      if (form.assigned_mechanic_id) {
        try {
          await axios.post(`${BASE}/inspections/${inspection_id}/assign`,
                           { mechanic_id: form.assigned_mechanic_id });
        } catch {
          toast.warning('Inspection created but mechanic assignment failed — use the table dropdown');
        }
      }
      toast.success(`Inspection created · ${r.data.wisedrive_id}`);
      reset();
      onCreated?.(inspection_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? 'Update failed' : 'Create failed'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && (onClose?.(), reset())}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="new-inspection-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit MFCWL Inspection' : 'New MFCWL Inspection'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: RC lookup — hidden in edit mode, RC number is not editable */}
          {!isEdit && (
          <div>
            <Label className="text-xs text-gray-500">Vehicle Registration Number</Label>
            <div className="flex gap-2 mt-1">
              <Input placeholder="e.g. KA03MN4567"
                     value={rc}
                     onChange={e => setRc(e.target.value.toUpperCase())}
                     className="font-mono"
                     onKeyDown={e => e.key === 'Enter' && lookupRc()}
                     data-testid="rc-input" />
              <Button onClick={lookupRc} disabled={rcLoading || !rc.trim()}
                      data-testid="rc-lookup-btn">
                {rcLoading ? <Loader2 className="h-4 w-4 animate-spin" />
                           : <><Search className="h-3.5 w-3.5 mr-1" /> Fetch</>}
              </Button>
            </div>
            <div className="text-[11px] text-gray-400 mt-1">
              Powered by Surepass · auto-fills Make, Model, Year, Fuel type from the RC database
            </div>
          </div>
          )}

          {isEdit && (
            <div className="bg-slate-50 border rounded p-2 text-xs">
              <span className="text-gray-500">Editing:</span>{' '}
              <span className="font-mono font-semibold">{initialInspection?.inspection_id}</span>{' '}
              · <span className="font-mono">{stripSpaces(form.vehicle_reg_no)}</span>
            </div>
          )}

          {rcFetched && (
            <>
              {/* Step 2: Vehicle details (editable, prefilled) */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Make">
                  <Input value={form.vehicle_make}
                         onChange={e => setForm(f => ({ ...f, vehicle_make: e.target.value }))}
                         data-testid="field-make" />
                </Field>
                <Field label="Model">
                  <Input value={form.vehicle_model}
                         onChange={e => setForm(f => ({ ...f, vehicle_model: e.target.value }))}
                         data-testid="field-model" />
                </Field>
                <Field label="Year">
                  <Input type="number" value={form.vehicle_year}
                         onChange={e => setForm(f => ({ ...f, vehicle_year: e.target.value }))}
                         data-testid="field-year" />
                </Field>
                <Field label="Fuel type">
                  <Select value={form.fuel_type} onValueChange={v => setForm(f => ({ ...f, fuel_type: v }))}>
                    <SelectTrigger data-testid="field-fuel"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{FUELS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Transmission *">
                  <Select value={form.transmission} onValueChange={v => setForm(f => ({ ...f, transmission: v }))}>
                    <SelectTrigger data-testid="field-transmission"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{TRANSMISSIONS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Kms driven *">
                  <Input type="number" value={form.km_driven}
                         placeholder="e.g. 48000"
                         onChange={e => setForm(f => ({ ...f, km_driven: e.target.value }))}
                         data-testid="field-km" />
                </Field>
                <Field label="Has Turbo?" full>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={!form.has_turbo}
                             onChange={() => setForm(f => ({ ...f, has_turbo: false }))}
                             data-testid="field-turbo-no" /> No
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={!!form.has_turbo}
                             onChange={() => setForm(f => ({ ...f, has_turbo: true }))}
                             data-testid="field-turbo-yes" /> Yes
                    </label>
                  </div>
                </Field>
              </div>

              {/* Step 3: Customer + Location */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Customer name *">
                  <Input value={form.customer_name}
                         onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                         data-testid="field-customer-name" />
                </Field>
                <Field label="Customer phone">
                  <Input value={form.customer_phone}
                         onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                         data-testid="field-customer-phone" />
                </Field>
                <Field label="Address *" full>
                  <PlacesAutocomplete
                    value={form.location_address}
                    onChange={v => setForm(f => ({ ...f, location_address: v }))}
                    onSelect={onPlaceSelect}
                    placeholder="Search Google Maps…"
                    country="in"
                  />
                  {form.location_city && (
                    <div className="text-[11px] text-gray-500 mt-1">
                      City: <b>{form.location_city}</b>
                      {form.location_lat && ` · ${form.location_lat.toFixed(4)}, ${form.location_lng.toFixed(4)}`}
                    </div>
                  )}
                </Field>
                <Field label="Pincode">
                  <Input value={form.location_pincode}
                         onChange={e => setForm(f => ({ ...f, location_pincode: e.target.value }))}
                         data-testid="field-pincode" />
                </Field>
                <Field label="City">
                  <Input value={form.location_city}
                         onChange={e => setForm(f => ({ ...f, location_city: e.target.value, assigned_mechanic_id: '' }))}
                         placeholder="Auto-filled from address"
                         data-testid="field-city" />
                </Field>
                <Field label="Assign mechanic" full>
                  <MechanicSelectInModal
                    mechanics={mechanics}
                    city={form.location_city}
                    value={form.assigned_mechanic_id}
                    onChange={v => setForm(f => ({ ...f, assigned_mechanic_id: v }))}
                  />
                </Field>
              </div>

              {/* Step 4: Schedule + tier */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Scheduled at *">
                  <Input type="datetime-local"
                         value={form.scheduled_at}
                         min={todayLocalIso()}
                         onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
                         data-testid="field-scheduled" />
                  <div className="text-[10px] text-gray-400 mt-0.5">Times shown in your local timezone</div>
                </Field>
                <Field label="Service tier">
                  <Select value={form.service_tier} onValueChange={v => setForm(f => ({ ...f, service_tier: v }))}>
                    <SelectTrigger data-testid="field-tier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manpower_tech">Manpower + Tech (₹600)</SelectItem>
                      <SelectItem value="manpower_tech_history">Manpower + Tech + Service History (₹800)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Notes" full>
                  <Input value={form.notes}
                         placeholder="Optional — internal notes"
                         onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </Field>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button variant="outline" onClick={() => { onClose?.(); reset(); }}>Cancel</Button>
                <Button onClick={submit} disabled={submitting}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="submit-inspection-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {isEdit ? 'Update' : 'Save & Assign'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <Label className="text-xs text-gray-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Today at 00:00 in the user's local timezone — usable as <input min=...>
function todayLocalIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 16);
}

// ── City alias map ─────────────────────────────────────────────────
// Indian cities are routinely written in two or three different ways
// (anglicised colonial name vs. native spelling, with/without spaces,
// etc.). The mechanic master in HR is seeded by humans, the inspection's
// `location_city` is populated by Google Places auto-fill — so the two
// strings frequently disagree even though they refer to the SAME city.
//
// This map normalises every spelling we've seen in production to a
// single canonical token, so the mechanic dropdown matches correctly.
// Add a new key whenever a partner / mechanic reports a missing match.
const CITY_ALIAS = {
  bangalore:   'bengaluru',
  bengaluru:   'bengaluru',
  bombay:      'mumbai',
  mumbai:      'mumbai',
  madras:      'chennai',
  chennai:     'chennai',
  calcutta:    'kolkata',
  kolkata:     'kolkata',
  gurgaon:     'gurugram',
  gurugram:    'gurugram',
  poona:       'pune',
  pune:        'pune',
  baroda:      'vadodara',
  vadodara:    'vadodara',
  trivandrum:  'thiruvananthapuram',
  thiruvananthapuram: 'thiruvananthapuram',
  cochin:      'kochi',
  kochi:       'kochi',
};

function canonicalCity(raw) {
  const s = (raw || '').toLowerCase().trim().replace(/\s+/g, ' ');
  return CITY_ALIAS[s] || s;
}

// Mechanic dropdown filtered by city (picks from the already-loaded list)
function MechanicSelectInModal({ mechanics, city, value, onChange }) {
  const cityNorm = canonicalCity(city);
  const eligible = cityNorm
    ? mechanics.filter(m =>
        (m.inspection_cities || []).some(c => canonicalCity(c) === cityNorm))
    : mechanics;
  const usingFallback = cityNorm && eligible.length === 0;
  const list = usingFallback ? mechanics : eligible;

  return (
    <div className="space-y-1">
      <Select value={value || '__unassigned__'} onValueChange={v => onChange(v === '__unassigned__' ? '' : v)}>
        <SelectTrigger data-testid="field-mechanic">
          <SelectValue placeholder={city ? `Mechanics in ${city}` : 'Pick a location first'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__unassigned__">— Leave unassigned —</SelectItem>
          {list.length === 0
            ? <SelectItem value="__none__" disabled>No mechanics available</SelectItem>
            : list.map(m => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name} {m.phone ? ` · ${m.phone}` : ''}
                  {(m.inspection_cities || []).length > 0 && (
                    <span className="text-gray-400 ml-1">
                      · {m.inspection_cities.slice(0, 2).join(', ')}
                    </span>
                  )}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
      {city && usingFallback && (
        <div className="text-[10px] text-amber-600">
          No mechanic mapped to <b>{city}</b> in HR — showing all mechanics as fallback
        </div>
      )}
    </div>
  );
}


function Row({ r, mechanics, onOpen, onEdit, onRefresh }) {
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const changeStatus = async (newStatus) => {
    if (newStatus === r.status) return;
    setUpdatingStatus(true);
    try {
      await axios.post(`${BASE}/inspections/${r.inspection_id}/status`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status update failed');
    } finally { setUpdatingStatus(false); }
  };

  const regNo = stripSpaces(r.vehicle?.reg_no);
  const makeModel = compactMakeModel(r.vehicle);
  const when = r.scheduled_at || r.created_at;

  return (
    <tr className="border-t hover:bg-indigo-50/40" data-testid={`mfcwl-row-${r.inspection_id}`}>
      <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">
        <div className="font-medium">{fmtDateOnly(when)}</div>
        <div className="text-gray-500">{fmtTimeOnly(when)}</div>
      </td>
      <td className="px-3 py-2">
        <span className="font-mono text-xs text-slate-700">{r.inspection_id}</span>
      </td>
      <td className="px-3 py-2">
        <WdsIdBadge wisedriveId={r.wisedrive_id} />
      </td>
      <td className="px-3 py-2">
        <div className="font-medium">{r.customer?.name}</div>
        <div className="text-xs text-gray-500">{r.customer?.phone}</div>
      </td>
      <td className="px-3 py-2">
        {r.location?.city ? (
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center gap-1 text-sm hover:text-indigo-600 underline-offset-2 hover:underline"
                      data-testid={`city-${r.inspection_id}`}>
                <MapPin className="h-3.5 w-3.5 text-gray-400" />
                {r.location.city}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 text-xs" side="right" align="start">
              <div className="font-semibold text-gray-800 mb-2">Full address</div>
              <div className="space-y-1 text-gray-700">
                <div>{r.location?.address}</div>
                <div className="text-gray-500">
                  {r.location?.city}
                  {r.location?.pincode && ` · ${r.location.pincode}`}
                </div>
                {(r.location?.lat != null && r.location?.lng != null) && (
                  <a
                    href={`https://maps.google.com/?q=${r.location.lat},${r.location.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    Open in Google Maps <MapPin className="h-3 w-3" />
                  </a>
                )}
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs">
        {regNo ? (
          <>
            <div className="font-mono font-semibold text-sm">{regNo}</div>
            <div className="text-gray-500">{makeModel || '—'}</div>
          </>
        ) : (
          <div className="font-medium">{makeModel || '—'}</div>
        )}
      </td>
      <td className="px-3 py-2">
        {r.mechanic ? (
          <div className="flex items-center gap-2">
            <div className="text-xs min-w-0">
              <div className="font-medium truncate max-w-[160px]">{r.mechanic.name}</div>
              <div className="text-gray-500 truncate max-w-[160px]">{r.mechanic.phone || r.mechanic.email}</div>
            </div>
            <Button size="sm" variant="ghost"
                    className="h-6 w-6 p-0 shrink-0"
                    onClick={onEdit}
                    title="Edit inspection / change mechanic"
                    data-testid={`edit-${r.inspection_id}`}>
              <Edit3 className="h-3 w-3 text-gray-500 hover:text-indigo-600" />
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline"
                  onClick={onEdit}
                  className="h-8 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid={`edit-${r.inspection_id}`}>
            <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit details
          </Button>
        )}
      </td>
      <td className="px-3 py-2">
        <Select value={r.status} onValueChange={changeStatus} disabled={updatingStatus}>
          <SelectTrigger
            className={`w-36 h-7 text-[11px] border ${STATUS_COLOR[r.status] || 'bg-gray-100 text-gray-700'}`}
            data-testid={`status-${r.inspection_id}`}
          >
            <SelectValue placeholder={r.status} />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 w-[140px]">
        <Button size="sm" variant="outline" onClick={onOpen}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 h-7 text-[11px] gap-1"
                data-testid={`open-${r.inspection_id}`}>
          <Eye className="h-3.5 w-3.5" /> View & Edit
        </Button>
      </td>
    </tr>
  );
}

// ── Inspection detail drawer (Report / Upload / Complete) ────────────
function InspectionDrawer({ inspectionId, onClose, onRefresh }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);  // 'svc' | 'sound'
  const [activeTab, setActiveTab] = useState('engine');
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [rescanRequesting, setRescanRequesting] = useState(false);
  const [link, setLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);

  const load = useCallback(async () => {
    if (!inspectionId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/inspections/${inspectionId}/report.json`);
      setDoc(r.data);
    } catch { toast.error('Fetch failed'); }
    finally { setLoading(false); }
  }, [inspectionId]);

  const loadAudit = useCallback(async () => {
    if (!inspectionId) return;
    setAuditLoading(true);
    try {
      const r = await axios.get(`${BASE}/inspections/${inspectionId}/audit`);
      setAudit(r.data);
    } catch { /* silent — audit is non-critical */ }
    finally { setAuditLoading(false); }
  }, [inspectionId]);

  useEffect(() => { if (inspectionId) { load(); loadAudit(); } }, [inspectionId, load, loadAudit]);

  // Auto-refresh while AI analysis is in flight. The Auto-Inspect-Pro
  // background poller updates the inspection doc every ~30s; without
  // this, admin would have to manually click Refresh to see the result
  // land. We only poll on transitional states and stop the moment we
  // see a terminal status (COMPLETED / FAILED) or the drawer closes —
  // so it's a no-op for the vast majority of inspection views.
  useEffect(() => {
    if (!inspectionId || !doc) return;
    const status = doc?.inspection?.analysis_status || doc?.analysis_status;
    const transitional = ['QUEUED', 'MIRRORING_OBD', 'ANALYZING'];
    if (!transitional.includes(status)) return;
    const t = setInterval(() => { load(); loadAudit(); }, 15000);
    return () => clearInterval(t);
  }, [inspectionId, doc, load, loadAudit]);

  const issueLink = useCallback(async () => {
    setLinkLoading(true);
    try {
      const r = await axios.post(`${BASE}/inspections/${inspectionId}/public-link`, { ttl_days: 7 });
      setLink(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link generation failed');
    } finally { setLinkLoading(false); }
  }, [inspectionId]);

  const uploadServiceHistory = async (file) => {
    setUploading('svc');
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/service-history`, fd);
      toast.success('Service history uploaded + AI-extracted');
      load(); loadAudit(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  const uploadSoundAi = async (file) => {
    setUploading('sound');
    // Use the same orchestrated endpoint as the partner /engine-video
    // route. This gives us:
    //   • MP4/MP3/M4A/WAV all accepted (auto-converted to 16-bit mono
    //     16 kHz WAV via ffmpeg before submission to Auto-Inspect-Pro).
    //   • Auto-retry on transient submission failures.
    //   • Background poller that flips analysis_status → COMPLETED or
    //     FAILED on the inspection doc, so the UI status badge updates.
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/engine-video`, fd);
      toast.success('Engine sound submitted to AI pipeline');
      load(); loadAudit(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  const reanalyzeEngineAi = async () => {
    setReanalyzing(true);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/reanalyze`);
      toast.success('Re-analysis queued — new version in progress');
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-analyse failed'); }
    finally { setReanalyzing(false); }
  };

  const requestObdRescan = async () => {
    if (!window.confirm('Request the mechanic to rescan OBD? The current scan stays in history.')) return;
    setRescanRequesting(true);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/request-rescan`);
      toast.success('Rescan requested — mechanic will be prompted on next app open');
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rescan request failed'); }
    finally { setRescanRequesting(false); }
  };

  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const runObdAiAnalyze = async () => {
    setAiAnalyzing(true);
    try {
      const r = await axios.post(`${BASE}/inspections/${inspectionId}/obd-ai-analyze`);
      const total = r.data?.summary?.total ?? 0;
      toast.success(`AI Analysis complete: ${total} verified code${total !== 1 ? 's' : ''}`);
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'AI analysis failed'); }
    finally { setAiAnalyzing(false); }
  };

  const markComplete = async () => {
    if (!window.confirm('Mark as COMPLETED? Invoice line will be created.')) return;
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/complete`, { verdict: 'PASS' });
      toast.success('Inspection completed');
      load(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const insp = doc?.inspection;
  const events = audit?.events || [];

  return (
    <Dialog open={!!inspectionId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-[98vw] w-[98vw] h-[98vh] max-h-[98vh] p-0 rounded-xl overflow-hidden flex flex-col bg-slate-50 gap-0"
        data-testid="mfcwl-drawer">
        {/* Full-page spinner only on the initial load when there's
            no doc yet to show. Subsequent background refreshes (after
            an upload / re-analysis) keep the existing content visible
            and surface a small "Refreshing…" pill in the header below,
            so the dialog never blanks out mid-action. */}
        {!doc ? (
          <div className="flex-1 grid place-items-center text-slate-400"
               data-testid="mfcwl-drawer-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Background-refresh indicator (top-right, non-blocking) */}
            {loading && (
              <div className="absolute top-2 right-14 z-10 flex items-center gap-1.5
                              bg-white/95 border border-slate-200 rounded-full
                              px-2.5 py-1 text-[11px] text-slate-600 shadow-sm"
                   data-testid="mfcwl-drawer-refreshing">
                <Loader2 className="h-3 w-3 animate-spin text-indigo-600" />
                Refreshing…
              </div>
            )}
            {/* ── Sticky Header ────────────────────────────────────── */}
            <div className="flex items-start justify-between gap-6 px-6 py-4 bg-white border-b border-slate-200 shrink-0">
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-xl font-semibold text-slate-900 tracking-tight truncate">
                  {insp?.customer?.name} <span className="text-slate-400 font-normal">·</span>{' '}
                  <span className="font-mono text-base text-slate-600">{insp?.inspection_id}</span>
                </DialogTitle>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <WdsIdBadge wisedriveId={insp?.wisedrive_id} size="sm" />
                  <Badge className={STATUS_COLOR[insp?.status] || 'bg-gray-100'}>{insp?.status}</Badge>
                  <Badge variant="outline">{insp?.service_tier}</Badge>
                  <Badge variant="outline">₹{insp?.unit_price_inr}</Badge>
                  {insp?.mechanic?.name && <Badge variant="secondary">Mechanic: {insp.mechanic.name}</Badge>}
                </div>
              </div>

              {/* Compact vehicle + location summary */}
              <div className="hidden md:grid grid-cols-2 gap-5 text-xs max-w-md">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-0.5">Vehicle</div>
                  <div className="text-slate-900">{insp?.vehicle?.make} {insp?.vehicle?.model} {insp?.vehicle?.year}</div>
                  {insp?.vehicle?.reg_no && <div className="font-mono text-slate-700">{stripSpaces(insp.vehicle.reg_no)}</div>}
                  {insp?.vehicle?.vin && <div className="text-slate-500 font-mono text-[10px] truncate">{insp.vehicle.vin}</div>}
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-0.5">Location</div>
                  <div className="text-slate-900 truncate">{insp?.location?.address}</div>
                  <div className="text-slate-500">{insp?.location?.city} {insp?.location?.pincode}</div>
                </div>
              </div>

              {/* Primary action — right-margin gives space for the Dialog's built-in X close button (positioned at right-4 top-4) */}
              {insp?.status !== 'COMPLETED' && (
                <Button onClick={markComplete} data-testid="mark-complete-btn"
                        className="bg-blue-600 hover:bg-blue-700 text-white mr-8">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Mark as COMPLETED
                </Button>
              )}
            </div>

            {/* ── Body: vertical sidebar + scrollable content ─────── */}
            <div className="flex flex-1 min-h-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}
                    className="flex flex-1 min-h-0" orientation="vertical">
                {/* Left sidebar nav */}
                <div className="w-56 shrink-0 border-r border-slate-200 bg-white overflow-y-auto p-3">
                  <TabsList
                    className="flex flex-col items-stretch h-auto bg-transparent p-0 gap-1 w-full"
                    data-testid="mfcwl-drawer-tabs">
                    <TabsTrigger value="engine"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-engine-ai">
                      <Volume2 className="h-4 w-4" /> Engine AI
                    </TabsTrigger>
                    <TabsTrigger value="obd"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-obd2">
                      <Cpu className="h-4 w-4" /> OBD2
                    </TabsTrigger>
                    <TabsTrigger value="service"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-service-history">
                      <History className="h-4 w-4" /> Service History
                    </TabsTrigger>
                    <TabsTrigger value="reports"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-reports">
                      <FileText className="h-4 w-4" /> Reports
                    </TabsTrigger>
                    <TabsTrigger value="activity"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-activity-notes">
                      <MessageSquare className="h-4 w-4" /> Activity &amp; Notes
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Right content */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50">
                  <div className="max-w-7xl mx-auto w-full space-y-6 text-sm">
                    {/* ─── Engine AI ─── */}
                    <TabsContent value="engine" className="mt-0 space-y-4 focus-visible:ring-0">
                      <CombinedVerdictCard doc={doc} />
                      <EngineAiTab
                        inspectionId={inspectionId}
                        doc={doc}
                        uploading={uploading}
                        onUpload={uploadSoundAi}
                        onReanalyze={reanalyzeEngineAi}
                        reanalyzing={reanalyzing}
                      />
                      <AutoInspectProCard doc={doc} />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="engine" testIdPrefix="engine" />
                    </TabsContent>

                    {/* ─── OBD2 ─── */}
                    {/* For MFCWL the OBD data is delivered as a partner-uploaded
                        PDF (no live scanner). We intentionally render only the
                        PDF preview + audit trail here. */}
                    <TabsContent value="obd" className="mt-0 space-y-4 focus-visible:ring-0">
                      <CarpmObdReport doc={doc} />
                      <ObdPdfCard doc={doc} inspectionId={inspectionId}
                                  onRefresh={() => { load(); loadAudit(); }} />
                      <SendToMfcwlCard inspectionId={inspectionId}
                                       onRefresh={() => { loadAudit(); }} />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="obd" testIdPrefix="obd" />
                    </TabsContent>

                    {/* ─── Service History ─── */}
                    <TabsContent value="service" className="mt-0 space-y-4 focus-visible:ring-0">
                      <LiveProgressServiceHistoryTab
                        inspection={{
                          id: inspectionId,
                          kms_driven: insp?.vehicle?.km_driven,
                          vehicle_make: insp?.vehicle?.make,
                          vehicle_model: insp?.vehicle?.model,
                          car_number: insp?.vehicle?.reg_no,
                          vin_number: insp?.vehicle?.vin,
                        }}
                        currentOdometer={insp?.vehicle?.km_driven}
                        apiBase="/api/b2b/mfcwl/admin/inspections"
                        tabValue="service"
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="service" testIdPrefix="service" />
                    </TabsContent>

                    {/* ─── Reports ─── */}
                    <TabsContent value="reports" className="mt-0 space-y-4 focus-visible:ring-0">
                      <ReportsDeliveryCard
                        inspectionId={inspectionId}
                        doc={doc}
                        link={link}
                        linkLoading={linkLoading}
                        onIssueLink={issueLink}
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="all" testIdPrefix="reports" />
                    </TabsContent>

                    {/* ─── Activity & Notes ─── */}
                    <TabsContent value="activity" className="mt-0 space-y-4 focus-visible:ring-0">
                      <ActivityNotesTab
                        inspectionId={inspectionId}
                        events={events}
                        loading={auditLoading}
                        onRefresh={loadAudit}
                      />
                    </TabsContent>
                  </div>
                </div>
              </Tabs>
            </div>
          </>
        )}
      </DialogContent>

    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Sub-tab: Engine AI
// ═══════════════════════════════════════════════════════════════════
function EngineAiTab({ inspectionId, doc, uploading, onUpload, onReanalyze, reanalyzing }) {
  const insp = doc?.inspection || {};
  const analyses = insp.analyses || [];
  const latest = insp.latest_version_id
    ? analyses.find(a => a.version_id === insp.latest_version_id)
    : (analyses.length ? analyses[analyses.length - 1] : null);
  const analysisStatus = insp.analysis_status || 'AWAITING_VIDEO';
  const hasAudioOnDisk = analyses.some(a => a.audio_file_path);

  return (
    <div className="space-y-3" data-testid="engine-ai-tab">
      <Card><CardContent className="p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="font-semibold flex items-center gap-1.5">
            <Volume2 className="h-4 w-4 text-indigo-600" /> Engine Sound AI
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`text-[10px] ${ANALYSIS_STATUS_COLOR[analysisStatus] || 'bg-gray-100 text-gray-500'}`}>
              {ANALYSIS_STATUS_LABEL[analysisStatus] || analysisStatus}
            </Badge>
            <label className="cursor-pointer">
              <input type="file" accept="audio/*,.wav,.mp3,.m4a,.mp4" className="hidden"
                     onChange={e => e.target.files?.[0] && onUpload(e.target.files[0])}
                     data-testid="upload-sound-input" />
              <Button size="sm" variant="outline" disabled={uploading === 'sound'} asChild>
                <span>{uploading === 'sound'
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Uploading…</>
                  : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload audio</>}</span>
              </Button>
            </label>
            <Button size="sm" variant="default" className="bg-violet-600 hover:bg-violet-700"
                    disabled={reanalyzing || !hasAudioOnDisk}
                    onClick={onReanalyze}
                    title={hasAudioOnDisk ? 'Re-run AI on the most recent audio' : 'No audio on disk to re-analyse'}
                    data-testid="reanalyze-engine-ai-btn">
              {reanalyzing
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Re-analysing…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Re-analyse</>}
            </Button>
          </div>
        </div>

        {analyses.length === 0 ? (
          <p className="text-xs text-gray-400 italic">
            No engine sound submitted yet. Mechanic uploads via the field app, or admin via "Upload audio".
          </p>
        ) : (
          <>
            {/* Latest result preview */}
            {latest && (
              <div className="text-xs border rounded p-2 bg-violet-50/50 mb-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-violet-900">
                    Latest version · <code className="text-[10px]">{latest.version_id}</code>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{latest.status}</Badge>
                </div>
                <div className="text-[10px] text-gray-600">
                  File: {latest.audio_filename || '—'} · uploaded {fmtDate(latest.created_at)}
                </div>
              </div>
            )}
            {/* Version history */}
            {analyses.length > 1 && (
              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Version history</div>
                <div className="space-y-1 max-h-36 overflow-y-auto">
                  {[...analyses].reverse().map((a, i) => (
                    <div key={a.version_id || i} className="flex items-center justify-between text-[11px] border rounded px-2 py-1 bg-white">
                      <span className="font-mono text-gray-600">{a.version_id}</span>
                      <Badge variant="outline" className="text-[9px]">{a.status}</Badge>
                      <span className="text-gray-500 text-[10px]">{fmtDate(a.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Raw Sound AI JSON */}
            {doc.sound_ai && (
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-600 hover:text-indigo-600">Raw Sound AI payload</summary>
                <pre className="text-[10px] bg-gray-50 p-2 rounded max-h-60 overflow-auto mt-1">
{JSON.stringify(doc.sound_ai, null, 2).slice(0, 2500)}
                </pre>
              </details>
            )}
          </>
        )}
      </CardContent></Card>

      {/* ═════════════════════════════════════════════════════════════
           Wisedrive Sound AI — full engine analysis sections.
           Mirrors the public MFCWL report UI (ai.wisedrive.com) so
           admin sees exactly what the partner / customer sees.
           Only rendered when the latest version has COMPLETED with a
           full AI result payload.
         ═════════════════════════════════════════════════════════════ */}
      {latest?.status === 'COMPLETED' && latest?.result && (
        <EngineAiSections result={latest.result} />
      )}
    </div>
  );
}

function EngineAiSections({ result }) {
  return (
    <div className="space-y-4" data-testid="engine-ai-sections">
      {/* Row 1 — compact gauge + narrative side-by-side on xl screens */}
      {(result.engine_rating || result.ai_analysis) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
          {result.engine_rating && <EngineRating rating={result.engine_rating} />}
          {result.ai_analysis && <WisedriveAnalysis aiAnalysis={result.ai_analysis} />}
        </div>
      )}

      {/* Row 2 — sound type analysis (chart-heavy, full width) */}
      {result.sound_classification && (
        <SoundTypeAnalysis data={result.sound_classification} />
      )}

      {/* Row 3 — frequency histogram + waveform (full width) */}
      {(result.histogram_data || result.histogram_image) && (
        <SoundHistogram
          data={result.histogram_data}
          histogramImage={result.histogram_image}
          waveformImage={result.waveform_image}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Sub-tab: OBD2 — mirrors the B2C "DTC Code Analysis" UX
// Scan status + rescan | DTC count banner | AI analysis with
// ECU-grouped, expandable code rows (causes / symptoms / actions).
// ═══════════════════════════════════════════════════════════════════
function ObdTab({ doc, onRequestRescan, rescanRequesting, onAiAnalyze, aiAnalyzing, inspectionId, onRefresh }) {
  const obd = doc?.obd;
  const insp = doc?.inspection || {};
  const rescanPending = !!insp.obd_rescan_requested;
  const aiAnalysis = insp.obd_ai_analysis || null;

  // PDF fallback upload (admin-only; canEdit is effectively admin here)
  const fileInputRef = useRef(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const handleObdPdfUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !inspectionId) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('PDF must be under 10 MB');
      return;
    }
    setUploadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('pdf', file);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/inspections/${inspectionId}/obd-pdf-upload`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        }
      );
      if (res.data?.success) {
        const n = (res.data.dtc_codes || []).length;
        toast.success(
          n > 0
            ? `Extracted ${n} DTC code${n !== 1 ? 's' : ''} from ${file.name}`
            : `No DTCs found in ${file.name} — modules summary saved`
        );
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'PDF upload failed');
    } finally {
      setUploadingPdf(false);
    }
  };

  // Live sensor readings — passthrough from the mechanic's scan when present.
  const liveData = obd?.raw?.live_data || obd?.live_data || [];

  // Raw count — sum of buckets so we know if "Analyze Codes" should be enabled
  const rawCount = useMemo(() => {
    if (!obd) return 0;
    const d = obd.dtc || {};
    return (d.critical?.length || 0) + (d.warning?.length || 0) + (d.info?.length || 0);
  }, [obd]);

  // Verified (AI-enriched) codes with normalized status
  const verified = useMemo(() => {
    if (!aiAnalysis) return [];
    return (aiAnalysis.codes_for_report || []).map(c => ({
      ...c,
      ai_severity: c.ai_severity === 'IMPORTANT' ? 'CRITICAL' : (c.ai_severity === 'CRITICAL' ? 'CRITICAL' : 'MINOR'),
      status_label: c.status === 'Pending' || c.status === 'pending' ? 'Pending'
        : c.status === 'Active' || c.status === 'active' || c.status === 'Confirmed' ? 'Active'
        : 'History',
    }));
  }, [aiAnalysis]);

  // Group by ECU source (fallback: derive from code prefix)
  const grouped = useMemo(() => {
    const g = {};
    verified.forEach(c => {
      const sys = c.ecuSource || c.ecu_source
        || (c.code?.startsWith('P') ? 'Engine Control Module (ECM)'
           : c.code?.startsWith('C') ? 'Chassis/ABS'
           : c.code?.startsWith('B') ? 'Body Control'
           : 'Network/Other');
      if (!g[sys]) g[sys] = { name: sys, codes: [], critical: 0, minor: 0 };
      g[sys].codes.push(c);
      if (c.ai_severity === 'CRITICAL') g[sys].critical++; else g[sys].minor++;
    });
    return Object.values(g).sort((a, b) => b.critical - a.critical);
  }, [verified]);

  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedCode, setExpandedCode] = useState(null);

  // Open the first group by default so the user immediately sees the codes
  useEffect(() => {
    if (grouped.length && expandedGroups.size === 0) {
      setExpandedGroups(new Set([grouped[0].name]));
    }
  }, [grouped.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3" data-testid="obd-tab">
      {/* ── PDF fallback upload (admin) ───────────────────────────────── */}
      <div
        className={`rounded-lg p-3 border-2 transition-colors ${
          !obd ? 'border-purple-300 bg-purple-50' : 'border-slate-200 bg-slate-50'
        }`}
        data-testid="obd-pdf-upload-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${!obd ? 'bg-purple-600' : 'bg-slate-400'}`}>
              <FileUp className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 text-sm">Upload OBD Report PDF</p>
              <p className="text-[11px] text-gray-600">
                {!obd
                  ? 'No scan yet? Upload a PDF — AI will extract DTC codes automatically.'
                  : 'Fallback: re-extract from a fresh PDF (replaces the latest scan).'}
              </p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleObdPdfUpload}
            style={{ display: 'none' }}
            data-testid="obd-pdf-upload-input"
          />
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPdf}
            className={`${!obd ? 'bg-purple-600 hover:bg-purple-700' : 'bg-slate-600 hover:bg-slate-700'} text-white`}
            data-testid="obd-pdf-upload-btn"
          >
            {uploadingPdf ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing PDF…</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" />Upload PDF</>
            )}
          </Button>
        </div>
      </div>

      {/* ── Row: Scan status card + DTC count banner ─────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div className={`rounded-lg p-3 border ${obd ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${obd ? 'bg-green-500' : 'bg-gray-300'}`}>
              <Gauge className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-gray-900">{obd ? 'Scan Done' : 'Pending'}</p>
              {obd?.completed_at && (
                <p className="text-[10px] text-gray-500 truncate">{fmtDate(obd.completed_at)}</p>
              )}
            </div>
          </div>
          <Button
            variant={obd ? 'outline' : 'default'}
            size="sm"
            onClick={onRequestRescan}
            disabled={rescanRequesting || !obd}
            className={`w-full mt-2 text-xs ${obd ? '' : 'bg-blue-600 hover:bg-blue-700'}`}
            data-testid="request-rescan-btn">
            {rescanRequesting
              ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Requesting…</>
              : <><RotateCcw className="h-3 w-3 mr-1" /> Rescan</>}
          </Button>
          {rescanPending && (
            <div className="mt-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 text-center">
              Rescan pending
            </div>
          )}
        </div>
        <div className={`lg:col-span-3 p-3 rounded-lg border flex items-center gap-2 ${rawCount > 0 ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
          {rawCount > 0 ? (
            <>
              <AlertTriangle className="h-5 w-5 text-blue-600" />
              <span className="text-sm text-blue-800 font-medium">{rawCount} DTC code{rawCount !== 1 ? 's' : ''} found</span>
            </>
          ) : obd ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-sm text-green-700">No DTC codes detected</span>
            </>
          ) : (
            <>
              <Gauge className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-500 italic">No OBD scan linked yet</span>
            </>
          )}
        </div>
      </div>

      {/* ── DTC Code Analysis ─────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold flex items-center gap-1.5 text-sm">
              <Zap className="h-4 w-4 text-indigo-600" /> DTC Code Analysis
            </div>
            <Badge className="text-[10px]" variant={aiAnalysis ? 'default' : 'outline'}>
              {aiAnalysis ? `${verified.length} verified` : 'Not analyzed'}
            </Badge>
          </div>

          {/* Status bar: analyzed-timestamp + Re-analyze button */}
          <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border mb-3">
            <div className="text-xs text-gray-600">
              {aiAnalysis ? (
                <span>Analyzed: {fmtDate(aiAnalysis.analyzed_at)} <span className="text-gray-400">·</span> Manual</span>
              ) : rawCount > 0 ? (
                <span>Run analysis to classify {rawCount} raw code{rawCount !== 1 ? 's' : ''}</span>
              ) : (
                <span className="italic text-gray-400">No codes to analyze</span>
              )}
            </div>
            <Button
              variant="outline" size="sm"
              onClick={onAiAnalyze}
              disabled={aiAnalyzing || rawCount === 0}
              className="border-purple-300 text-purple-700 hover:bg-purple-50 h-7 text-[11px]"
              data-testid="obd-ai-analyze-btn">
              {aiAnalyzing
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Analyzing…</>
                : <><Zap className="h-3.5 w-3.5 mr-1.5" /> {aiAnalysis ? 'Re-analyze' : 'Analyze Codes'}</>}
            </Button>
          </div>

          {/* Grouped, expandable codes */}
          {aiAnalysis && (verified.length > 0 ? (
            <div className="space-y-2" data-testid="mfcwl-verified-codes">
              {grouped.map((group, gi) => {
                const isOpen = expandedGroups.has(group.name);
                return (
                  <div key={gi} className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-100 px-3 py-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-200"
                         onClick={() => setExpandedGroups(prev => { const s = new Set(prev); s.has(group.name) ? s.delete(group.name) : s.add(group.name); return s; })}>
                      <div className="flex items-center gap-2.5">
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                        <div className="h-8 w-8 rounded-lg bg-slate-700 flex items-center justify-center">
                          <span className="text-white font-bold text-[10px]">
                            {(group.name.match(/\b\w/g) || []).slice(0, 3).join('').toUpperCase() || group.name.slice(0, 3).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-gray-900">{group.name}</p>
                          <p className="text-[11px] text-gray-500">{group.codes.length} code{group.codes.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        {group.critical > 0 && <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-100 text-red-700 border border-red-200 font-medium">{group.critical} Critical</span>}
                        {group.minor > 0 && <span className="px-2 py-0.5 text-[10px] rounded-full bg-sky-100 text-sky-700 border border-sky-200 font-medium">{group.minor} Minor</span>}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="divide-y">
                        {group.codes.map((c, ci) => {
                          const isExp = expandedCode === c.code;
                          const hasCauses = c.possible_causes?.length > 0;
                          const hasSymp = c.symptoms?.length > 0;
                          const hasAct = c.recommended_actions?.length > 0;
                          const hasDetails = hasCauses || hasSymp || hasAct;
                          return (
                            <div key={ci} className={ci % 2 ? 'bg-gray-50/50' : 'bg-white'} data-testid={`dtc-row-${c.code}`}>
                              <div className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
                                   onClick={() => setExpandedCode(isExp ? null : c.code)}>
                                <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${c.ai_severity === 'CRITICAL' ? 'bg-red-100' : 'bg-sky-100'}`}>
                                  <CircleDot className={`h-3.5 w-3.5 ${c.ai_severity === 'CRITICAL' ? 'text-red-600' : 'text-sky-600'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-mono font-bold text-slate-800 text-sm">{c.code}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                      c.ai_severity === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-700'
                                    }`}>{c.ai_severity}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                      c.status_label === 'Active' ? 'bg-red-50 text-red-600'
                                        : c.status_label === 'Pending' ? 'bg-orange-50 text-orange-600'
                                        : 'bg-blue-50 text-blue-600'
                                    }`}>{c.status_label}</span>
                                    {hasDetails && (
                                      <Eye className={`h-3 w-3 ml-1 ${isExp ? 'text-blue-500' : 'text-gray-300'}`} />
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                                    {c.ai_explanation || c.description || 'No description'}
                                  </p>
                                </div>
                              </div>
                              {isExp && hasDetails && (
                                <div className="px-3 pb-3 pt-0 ml-10 space-y-2">
                                  {hasCauses && <DetailBlock title="Possible Causes" items={c.possible_causes} color="amber" icon={AlertTriangle} />}
                                  {hasSymp   && <DetailBlock title="Symptoms" items={c.symptoms} color="blue" icon={Eye} />}
                                  {hasAct    && <DetailBlock title="Recommended Actions" items={c.recommended_actions} color="emerald" icon={Wrench} />}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 font-medium">No fault codes detected</p>
              <p className="text-xs text-gray-400 mt-1">The vehicle's OBD is clean — nothing to analyse.</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Live Sensor Data ──────────────────────────────────────── */}
      {liveData.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-1.5 text-sm">
                <Activity className="h-4 w-4 text-blue-600" /> Live Sensor Data
              </div>
              <Badge variant="outline" className="text-[10px]">{liveData.length} reading{liveData.length !== 1 ? 's' : ''}</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="obd-live-data-grid">
              {liveData.map((r, idx) => {
                const IconComp = getMfcwlLiveIcon(r.pid);
                return (
                  <div key={idx} className="bg-white rounded-lg border border-slate-200 p-3 hover:border-blue-300 hover:shadow-sm transition-all"
                       data-testid={`live-data-reading-${r.pid || idx}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-7 w-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <IconComp className="h-3.5 w-3.5 text-blue-600" />
                      </div>
                      <span className="text-xs text-slate-500 font-medium truncate">{r.name || r.pid}</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-slate-900">
                        {typeof r.value === 'number'
                          ? r.value.toFixed(r.value % 1 === 0 ? 0 : 1)
                          : (r.value ?? '—')}
                      </span>
                      {r.unit && <span className="text-xs text-slate-400 font-medium">{r.unit}</span>}
                    </div>
                    {r.pid && <span className="text-[10px] text-slate-400 font-mono mt-1 block">PID: {r.pid}</span>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Scan History (accordion) ───────────────────────────────── */}
      {inspectionId && (
        <ScanHistory inspectionId={inspectionId}
                     apiBase="/api/b2b/mfcwl/admin/inspections" />
      )}

      {/* Scan metadata footer — always useful for debugging */}
      {obd && (
        <Card>
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Scan metadata</div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="border rounded p-2 bg-gray-50">
                <div className="text-gray-500">Scan ID</div>
                <div className="font-mono truncate">{obd.scan_id}</div>
              </div>
              <div className="border rounded p-2 bg-gray-50">
                <div className="text-gray-500">Partner verdict</div>
                <div className="font-medium">{obd.summary?.ai_verdict || '—'}</div>
              </div>
              <div className="border rounded p-2 bg-gray-50">
                <div className="text-gray-500">Total DTCs</div>
                <div className="font-medium">{obd.summary?.total ?? rawCount}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Live-data PID → icon map (mirrors B2C) ─────────────────────────
const MFCWL_LIVE_ICON = { '0C': Gauge, '0D': Activity, '05': Thermometer,
                          '11': Zap, '2F': Fuel, '0F': Wind, '1F': Timer };
function getMfcwlLiveIcon(pid) { return MFCWL_LIVE_ICON[pid] || Activity; }

// ── DetailBlock for Causes/Symptoms/Actions (shared with B2C aesthetic) ─
const MFCWL_DETAIL_STYLES = {
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   icon: 'text-amber-500',   title: 'text-amber-700',   dot: 'bg-amber-400' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    icon: 'text-blue-500',    title: 'text-blue-700',    dot: 'bg-blue-400' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-500', title: 'text-emerald-700', dot: 'bg-emerald-400' },
};

function DetailBlock({ title, items, color, icon: Icon }) {
  const s = MFCWL_DETAIL_STYLES[color] || MFCWL_DETAIL_STYLES.blue;
  return (
    <div className={`${s.bg} border ${s.border} rounded-lg p-2.5`}
         data-testid={`detail-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${s.icon}`} />
        <span className={`text-[11px] font-semibold ${s.title}`}>{title}</span>
      </div>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-700 leading-snug">
            <span className={`${s.dot} h-1.5 w-1.5 rounded-full mt-1.5 flex-shrink-0`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Sub-tab: Service History
// ═══════════════════════════════════════════════════════════════════
function ServiceHistoryTab({ doc, uploading, onUpload }) {
  const docs = doc?.service_history || [];
  return (
    <div className="space-y-3" data-testid="service-history-tab">
      <Card><CardContent className="p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold flex items-center gap-1.5">
            <History className="h-4 w-4 text-indigo-600" /> Service History
          </div>
          <label className="cursor-pointer">
            <input type="file" accept=".pdf,image/*" className="hidden"
                   onChange={e => e.target.files?.[0] && onUpload(e.target.files[0])}
                   data-testid="upload-svc-input" />
            <Button size="sm" variant="outline" disabled={uploading === 'svc'} asChild>
              <span>{uploading === 'svc'
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Uploading…</>
                : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload PDF / image</>}</span>
            </Button>
          </label>
        </div>
        {docs.length > 0 ? (
          <div className="space-y-2">
            {docs.map(sd => (
              <div key={sd.id} className="border rounded p-2 text-xs bg-gray-50">
                <div className="flex justify-between items-center">
                  <span className="font-medium truncate">{sd.original_filename}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {sd.extraction_status === 'done' ? `${sd.extracted_records?.length || 0} records` : sd.extraction_status}
                  </Badge>
                </div>
                {sd.extracted_records && sd.extracted_records.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {sd.extracted_records.slice(0, 6).map((rec, i) => (
                      <li key={i} className="text-[11px] text-gray-700">
                        {rec.date} · {rec.odometer_km} km · {rec.service_type}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">
            No service history uploaded yet. Drop any job-card PDF or photo — we'll AI-extract the records.
          </p>
        )}
      </CardContent></Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Shared: Audit Trail (scoped per tab)
// ═══════════════════════════════════════════════════════════════════
const AUDIT_SCOPE_PREFIXES = {
  engine: ['engine_video', 'engine_ai', 'sound_ai'],
  obd:    ['obd'],
  service:['service_history'],
  all:    null,   // show everything
};

function TabAuditTrail({ events, loading, onRefresh, scope, testIdPrefix }) {
  const prefixes = AUDIT_SCOPE_PREFIXES[scope];
  const filtered = !prefixes
    ? events
    : events.filter(e => prefixes.some(p => (e.action || '').includes(p)));

  return (
    <Card><CardContent className="p-3">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-medium text-gray-700">
          {scope === 'all' ? 'Full Audit Trail' : 'Audit Trail (scoped)'}
          <span className="ml-1.5 text-[10px] text-gray-400">
            {filtered.length} event{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
        <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                onClick={onRefresh} disabled={loading}
                data-testid={`${testIdPrefix}-refresh-audit`}>
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {loading && !events.length ? (
        <div className="text-[11px] text-gray-400 italic">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-[11px] text-gray-400 italic">No audit events for this scope yet.</div>
      ) : (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {filtered.map((e, i) => <AuditRow key={e.id || i} e={e} />)}
        </div>
      )}
    </CardContent></Card>
  );
}


// ═══════════════════════════════════════════════════════════════════
// REPORTS & DELIVERY — consolidated Web / PDF / JSON + audit trail
// ═══════════════════════════════════════════════════════════════════
function ReportsDeliveryCard({ inspectionId, doc, link, linkLoading, onIssueLink }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);

  const analysisStatus = doc?.inspection?.analysis_status || doc?.analysis_status || 'AWAITING_VIDEO';
  const ready = analysisStatus === 'COMPLETED';
  const combined = doc;

  useEffect(() => {
    if (ready && !link && !linkLoading) { onIssueLink(); }
  }, [ready, link, linkLoading, onIssueLink]);

  const dlPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/inspections/${inspectionId}/report.pdf`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      const safeReg = stripSpaces(doc?.inspection?.vehicle?.reg_no) || inspectionId;
      a.href = url; a.download = `MFCWL_Inspection_${safeReg}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF generation failed');
    } finally { setPdfLoading(false); }
  };

  const dlJson = () => {
    const blob = new Blob([JSON.stringify(combined, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${inspectionId}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (txt, label = 'Copied') => {
    try { navigator.clipboard.writeText(txt); toast.success(label); } catch { /* ignore */ }
  };

  return (
    <Card className="border border-indigo-100" data-testid="reports-delivery-card">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold flex items-center gap-1.5">
            <FileText className="h-4 w-4 text-indigo-600" /> Reports & Delivery
          </div>
          <Badge className={`text-[10px] ${ANALYSIS_STATUS_COLOR[analysisStatus] || 'bg-gray-100 text-gray-500'}`}>
            {ANALYSIS_STATUS_LABEL[analysisStatus] || analysisStatus}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {/* WEB */}
          <div className="border rounded-md p-2 bg-white">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
              <Globe className="h-3.5 w-3.5 text-indigo-600" /> Web Report
            </div>
            {!ready ? (
              <div className="text-[11px] text-gray-400">Available after AI analysis completes.</div>
            ) : link ? (
              <div className="space-y-1">
                <a href={link.web_url} target="_blank" rel="noreferrer"
                   className="text-[11px] text-indigo-600 hover:underline truncate block font-mono"
                   data-testid="drawer-web-link">
                  {link.web_url.replace(/^https?:\/\//, '').slice(0, 42)}…
                </a>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                          onClick={() => window.open(link.web_url, '_blank', 'noopener')}
                          data-testid="drawer-open-web">
                    Open new tab
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2"
                          onClick={() => copyToClipboard(link.web_url, 'Web URL copied')}>
                    Copy
                  </Button>
                </div>
                <div className="text-[9px] text-gray-400">Expires in {link.ttl_days} days</div>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={linkLoading}
                      onClick={onIssueLink} data-testid="drawer-issue-link">
                {linkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Generate link'}
              </Button>
            )}
          </div>

          {/* PDF */}
          <div className="border rounded-md p-2 bg-white">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
              <FileDown className="h-3.5 w-3.5 text-rose-600" /> MFCWL PDF
            </div>
            {!ready ? (
              <div className="text-[11px] text-gray-400">Available after AI analysis completes.</div>
            ) : (
              <div className="space-y-1">
                <div className="text-[10px] text-gray-500">MFCWL-branded inspection report</div>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        onClick={dlPdf} disabled={pdfLoading}
                        data-testid="drawer-download-pdf">
                  {pdfLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              : <Download className="h-3 w-3 mr-1" />} Download PDF
                </Button>
                {link?.pdf_url && (
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 ml-1"
                          onClick={() => copyToClipboard(link.pdf_url, 'PDF URL copied')}>
                    Copy tokenised URL
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* JSON */}
          <div className="border rounded-md p-2 bg-white">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
              <Code className="h-3.5 w-3.5 text-slate-700" /> JSON
            </div>
            <div className="space-y-1">
              <div className="text-[10px] text-gray-500">
                Combined inspection + OBD + AI payload
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        onClick={dlJson} data-testid="drawer-download-json">
                  <Download className="h-3 w-3 mr-1" /> Download
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                        onClick={() => setRawOpen(v => !v)}
                        data-testid="drawer-toggle-json">
                  {rawOpen ? 'Hide' : 'Preview'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {rawOpen && (
          <pre className="text-[10px] bg-slate-900 text-slate-100 p-2 rounded max-h-60 overflow-auto font-mono">
{JSON.stringify(combined, null, 2).slice(0, 4000)}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

const AUDIT_LABELS = {
  'mfcwl.partner.inspection.create':   { label: 'Inspection created by MFCWL',   icon: 'IN',  color: 'text-emerald-700' },
  'mfcwl.partner.inspection.poll':     { label: 'MFCWL polled inspection',       icon: 'PL',  color: 'text-slate-600' },
  'mfcwl.partner.engine_video.upload': { label: 'MFCWL uploaded engine video',   icon: 'EV',  color: 'text-indigo-700' },
  'mfcwl.partner.report.json.fetch':   { label: 'MFCWL pulled JSON report',      icon: 'JS',  color: 'text-blue-700' },
  'mfcwl.partner.report.pdf.fetch':    { label: 'MFCWL pulled PDF report',       icon: 'PD',  color: 'text-rose-700' },
  'mfcwl.inspection.assign':           { label: 'Mechanic assigned',             icon: 'AS',  color: 'text-indigo-700' },
  'mfcwl.inspection.status':           { label: 'Status changed',                icon: 'ST',  color: 'text-amber-700' },
  'mfcwl.engine_video.upload':         { label: 'Engine video uploaded (admin)', icon: 'EV',  color: 'text-indigo-700' },
  'mfcwl.admin.engine_ai.reanalyze':   { label: 'Re-analyse triggered (admin)',  icon: 'RA',  color: 'text-violet-700' },
  'mfcwl.admin.obd.rescan_request':    { label: 'OBD rescan requested (admin)',  icon: 'RS',  color: 'text-amber-700' },
  'mfcwl.admin.obd.ai_analyze':        { label: 'OBD AI analysis run (admin)',   icon: 'AI',  color: 'text-purple-700' },
  'mfcwl.mechanic.obd.submit':         { label: 'Mechanic submitted OBD scan',   icon: 'OB',  color: 'text-indigo-700' },
  'mfcwl.mechanic.engine_video.upload':{ label: 'Mechanic uploaded engine video',icon: 'EV',  color: 'text-indigo-700' },
  'mfcwl.mechanic.start':              { label: 'Mechanic started inspection',   icon: 'GO',  color: 'text-indigo-700' },
  'mfcwl.mechanic.complete':           { label: 'Mechanic marked complete',      icon: 'OK',  color: 'text-emerald-700' },
};

function AuditRow({ e }) {
  const [open, setOpen] = useState(false);
  const meta = AUDIT_LABELS[e.action] || { label: e.action, icon: '·', color: 'text-gray-600' };
  const actor = e.actor_type === 'partner'
    ? (e.actor_name || e.actor_id || 'partner')
    : (e.actor_email || 'admin');
  return (
    <div className="border rounded-sm bg-slate-50 p-1.5 text-[11px]">
      <button type="button" className="w-full text-left flex items-start gap-2"
              onClick={() => setOpen(v => !v)}>
        <span className="shrink-0 text-[9px] font-mono bg-slate-200 text-slate-700 rounded px-1 py-0.5">{meta.icon}</span>
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${meta.color}`}>
            {meta.label}
            {e.http_status && (
              <span className={`ml-1.5 text-[9px] px-1 rounded ${
                e.http_status >= 400 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                HTTP {e.http_status}
              </span>
            )}
          </div>
          <div className="text-[10px] text-gray-500 truncate">
            {actor} · {new Date(e.ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
        </div>
        <span className="text-gray-400 text-[10px]">{open ? '▲' : '▼'}</span>
      </button>
      {open && e.details && Object.keys(e.details).length > 0 && (
        <pre className="mt-1 text-[9px] bg-white p-1.5 rounded border font-mono overflow-auto max-h-28">
{JSON.stringify(e.details, null, 2)}
        </pre>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// TAB 2 — AI Analysis (engine video → Sound AI only)
// ═══════════════════════════════════════════════════════════════════
function AIAnalysisTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/engine-analyses`);
      setRows(r.data.analyses || []);
    } catch { toast.error('Failed to load analyses'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 space-y-3">
      <div className="text-xs text-gray-500">
        {rows.length} engine analyses · MFCWL uploads via <code>POST /api/b2b/mfcwl/engine-analyses</code>
      </div>
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-white" data-testid="mfcwl-analyses-table">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              {/* 6 columns of roughly equal width */}
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
            </colgroup>
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Date / Time</th>
                <th className="text-left px-3 py-2">Inspection ID</th>
                <th className="text-left px-3 py-2">Vehicle</th>
                <th className="text-left px-3 py-2">Engine video</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Report</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">No engine analyses received from MFCWL yet</td></tr>
              ) : rows.map(a => (
                <tr key={a.id} className="border-t hover:bg-indigo-50/40" data-testid={`analysis-row-${a.id}`}>
                  <td className="px-3 py-2 text-xs text-gray-700 align-top whitespace-nowrap">
                    <div className="font-medium">{fmtDateOnly(a.created_at)}</div>
                    <div className="text-gray-500">{fmtTimeOnly(a.created_at)}</div>
                  </td>
                  <td className="px-3 py-2 align-top font-mono text-[11px] text-gray-800 break-all">{a.inspection_id}</td>
                  <td className="px-3 py-2 align-top text-xs">
                    {a.vehicle?.reg_no ? (
                      <>
                        <div className="font-mono font-semibold text-sm">{stripSpaces(a.vehicle.reg_no)}</div>
                        <div className="text-gray-500">{compactMakeModel(a.vehicle) || '—'}</div>
                      </>
                    ) : (
                      <div className="font-medium">{compactMakeModel(a.vehicle) || '—'}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Button size="sm" variant="outline" className="h-7 text-[11px]"
                            onClick={async () => {
                              try {
                                const r = await axios.get(
                                  `${BASE}/engine-analyses/${a.id}/engine-video`,
                                  { responseType: 'blob' },
                                );
                                setVideoUrl(URL.createObjectURL(r.data));
                              } catch (e) { toast.error('Video unavailable'); }
                            }}
                            data-testid={`view-video-${a.id}`}>
                      <Play className="h-3.5 w-3.5 mr-1" /> View video
                    </Button>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <AiStatusCell analysis={a} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Button size="sm" variant="outline" onClick={() => setOpenId(a.id)}
                            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 h-7 text-[11px] gap-1"
                            data-testid={`open-analysis-${a.id}`}>
                      <Eye className="h-3.5 w-3.5" /> View & Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EngineAnalysisModal analysisId={openId} onClose={() => setOpenId(null)} onRefresh={load} />
      <VideoPreviewModal url={videoUrl} onClose={() => setVideoUrl(null)} />
    </div>
  );
}

// ── AI Analysis status mapping + timeline popover ────────────────────
//   1. Received            — partner uploaded; we persisted the file.
//   2. Processing          — AI pipeline running.
//   3. Processed           — AI returned a result; admin hasn't pushed back.
//   4. Analysis sent       — MFCWL fetched the final report (or webhook acked).
const AI_STATUS_LABEL = {
  received:       { label: 'Received',       color: 'bg-slate-100 text-slate-700 border-slate-200' },
  processing:     { label: 'Processing',     color: 'bg-amber-100 text-amber-700 border-amber-200' },
  processed:      { label: 'Processed',      color: 'bg-blue-100 text-blue-700 border-blue-200' },
  analysis_sent:  { label: 'Analysis sent',  color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  failed:         { label: 'Failed',         color: 'bg-rose-100 text-rose-700 border-rose-200' },
};

function deriveAiStatus(a) {
  if (a.status === 'failed') return 'failed';
  if (a.analysis_sent_at || a.delivered_at) return 'analysis_sent';
  if (a.status === 'completed' || a.completed_at) return 'processed';
  if (a.result) return 'processed';
  if (a.sound_ai_analysis_id) return 'processing';
  return a.created_at ? 'received' : 'processing';
}

function AiStatusCell({ analysis: a }) {
  const code = deriveAiStatus(a);
  const cfg = AI_STATUS_LABEL[code] || AI_STATUS_LABEL.processing;

  // Timeline — each stage either has a timestamp or "pending"
  const stages = [
    { code: 'received',      ts: a.created_at },
    { code: 'processing',    ts: a.ai_submitted_at || a.created_at },
    { code: 'processed',     ts: a.completed_at || (a.result ? a.created_at : null) },
    { code: 'analysis_sent', ts: a.analysis_sent_at || a.delivered_at },
  ];
  const activeIdx = stages.findIndex(s => s.code === code);

  return (
    <div className="flex items-center gap-1.5" data-testid={`ai-status-${a.id}`}>
      <span className={`px-2 py-0.5 rounded text-[10px] border font-medium whitespace-nowrap ${cfg.color}`}>
        {cfg.label}
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0"
                  title="Status history"
                  data-testid={`ai-status-log-${a.id}`}>
            <FileText className="h-3 w-3 text-gray-400 hover:text-indigo-600" />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="left" className="w-72 p-0" data-testid={`ai-status-popover-${a.id}`}>
          <div className="px-3 py-2 border-b text-xs font-semibold text-slate-700">
            Status timeline
          </div>
          <ol className="p-3 space-y-2">
            {stages.map((s, i) => {
              const stageCfg = AI_STATUS_LABEL[s.code];
              const done = !!s.ts && i <= activeIdx;
              const current = i === activeIdx;
              return (
                <li key={s.code} className="flex items-start gap-2.5">
                  <div className="relative flex flex-col items-center">
                    <span className={`h-2 w-2 rounded-full mt-1.5 ${
                      done ? 'bg-emerald-500' : current ? 'bg-amber-400' : 'bg-slate-300'
                    }`} />
                    {i < stages.length - 1 && (
                      <span className={`w-px flex-1 mt-1 ${done ? 'bg-emerald-300' : 'bg-slate-200'}`}
                            style={{ minHeight: '18px' }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className={`text-xs ${done ? 'font-medium text-slate-900' : 'text-slate-500'}`}>
                      {stageCfg.label}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      {s.ts ? fmtDate(s.ts) : (current ? 'In progress…' : 'Pending')}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function VideoPreviewModal({ url, onClose }) {
  const handleClose = () => {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
    onClose();
  };
  return (
    <Dialog open={!!url} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="video-preview-modal">
        <DialogHeader className="px-4 pt-3">
          <DialogTitle className="text-sm">Engine video</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4">
          {url && (
            <video src={url} controls autoPlay className="w-full rounded border bg-black"
                   data-testid="engine-video-player" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EngineAnalysisModal({ analysisId, onClose, onRefresh }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('engine');
  const [uploading, setUploading] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [link, setLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);

  const EA_BASE = `${BASE}/engine-analyses`;

  const load = useCallback(async () => {
    if (!analysisId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${EA_BASE}/${analysisId}/report.json`);
      setDoc(r.data);
    } catch { toast.error('Fetch failed'); }
    finally { setLoading(false); }
  }, [analysisId, EA_BASE]);

  const loadAudit = useCallback(async () => {
    if (!analysisId) return;
    setAuditLoading(true);
    try {
      const r = await axios.get(`${EA_BASE}/${analysisId}/audit`);
      setAudit(r.data);
    } catch { /* silent */ }
    finally { setAuditLoading(false); }
  }, [analysisId, EA_BASE]);

  useEffect(() => { if (analysisId) { load(); loadAudit(); } }, [analysisId, load, loadAudit]);

  const issueLink = useCallback(async () => {
    setLinkLoading(true);
    try {
      const r = await axios.post(`${EA_BASE}/${analysisId}/public-link`, { ttl_days: 7 });
      setLink(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Link generation failed'); }
    finally { setLinkLoading(false); }
  }, [analysisId, EA_BASE]);

  const uploadSoundAi = async (file) => {
    setUploading('sound');
    const fd = new FormData(); fd.append('audio_file', file);
    fd.append('inspection_id', doc?.inspection?.inspection_id || '');
    try {
      // Admin re-uploads aren't supported on engine-analyses today —
      // mechanics must re-submit via the partner endpoint. Fall back to
      // reanalyse-on-saved-file for admin convenience.
      toast.info('For AI-only analyses, please use Re-analyse to re-run on the existing audio.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      await axios.post(`${EA_BASE}/${analysisId}/reanalyze`);
      toast.success('Re-analysis queued');
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-analyse failed'); }
    finally { setReanalyzing(false); }
  };

  const insp = doc?.inspection;
  const events = audit?.events || [];

  return (
    <Dialog open={!!analysisId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-[98vw] w-[98vw] h-[98vh] max-h-[98vh] p-0 rounded-xl overflow-hidden flex flex-col bg-slate-50 gap-0"
        data-testid="mfcwl-analysis-modal">
        {loading || !doc ? (
          <div className="flex-1 grid place-items-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-6 px-6 py-4 bg-white border-b border-slate-200 shrink-0">
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-xl font-semibold text-slate-900 tracking-tight truncate">
                  {insp?.inspection_id} <span className="text-slate-400 font-normal">· AI only</span>
                </DialogTitle>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Badge className={STATUS_COLOR[insp?.status] || 'bg-gray-100'}>{insp?.status}</Badge>
                  <Badge variant="outline">{insp?.service_tier}</Badge>
                  <Badge variant="outline">₹{insp?.unit_price_inr}</Badge>
                  {analysisId && (
                    <Badge variant="secondary" className="font-mono text-[10px]">{analysisId.slice(0, 12)}</Badge>
                  )}
                </div>
              </div>
              <div className="hidden md:block text-xs max-w-lg text-right">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-0.5">Vehicle</div>
                {insp?.vehicle?.reg_no
                  ? <div className="font-mono font-semibold text-sm text-slate-900">{stripSpaces(insp.vehicle.reg_no)}</div>
                  : null}
                <div className="text-slate-500">{compactMakeModel(insp?.vehicle) || '—'}</div>
                <div className="text-slate-400 text-[10px]">
                  {[insp?.vehicle?.transmission, insp?.vehicle?.fuel_type,
                    insp?.vehicle?.km_driven != null && `${(insp.vehicle.km_driven / 1000).toFixed(0)}k km`,
                    insp?.vehicle?.has_turbo ? 'Turbo' : 'No turbo']
                    .filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>

            <div className="flex flex-1 min-h-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}
                    className="flex flex-1 min-h-0" orientation="vertical">
                <div className="w-56 shrink-0 border-r border-slate-200 bg-white overflow-y-auto p-3">
                  <TabsList className="flex flex-col items-stretch h-auto bg-transparent p-0 gap-1 w-full"
                            data-testid="mfcwl-analysis-modal-tabs">
                    <TabsTrigger value="engine"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="ea-tab-engine-ai">
                      <Volume2 className="h-4 w-4" /> Engine AI
                    </TabsTrigger>
                    <TabsTrigger value="service"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="ea-tab-service">
                      <History className="h-4 w-4" /> Service History
                    </TabsTrigger>
                    <TabsTrigger value="reports"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="ea-tab-reports">
                      <FileText className="h-4 w-4" /> Reports
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50">
                  <div className="max-w-7xl mx-auto w-full space-y-6 text-sm">
                    <TabsContent value="engine" className="mt-0 space-y-4 focus-visible:ring-0">
                      <EngineAiTab
                        inspectionId={analysisId}
                        doc={doc}
                        uploading={uploading}
                        onUpload={uploadSoundAi}
                        onReanalyze={reanalyze}
                        reanalyzing={reanalyzing}
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="engine" testIdPrefix="ea-engine" />
                    </TabsContent>

                    <TabsContent value="service" className="mt-0 space-y-4 focus-visible:ring-0">
                      <LiveProgressServiceHistoryTab
                        inspection={{
                          id: analysisId,
                          kms_driven: insp?.vehicle?.km_driven,
                          vehicle_make: insp?.vehicle?.make,
                          vehicle_model: insp?.vehicle?.model,
                          car_number: insp?.vehicle?.reg_no,
                        }}
                        currentOdometer={insp?.vehicle?.km_driven}
                        apiBase="/api/b2b/mfcwl/admin/engine-analyses"
                        tabValue="service"
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="service" testIdPrefix="ea-service" />
                    </TabsContent>

                    <TabsContent value="reports" className="mt-0 space-y-4 focus-visible:ring-0">
                      <ReportsDeliveryCard
                        inspectionId={analysisId}
                        doc={doc}
                        link={link}
                        linkLoading={linkLoading}
                        onIssueLink={issueLink}
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="all" testIdPrefix="ea-reports" />
                    </TabsContent>
                  </div>
                </div>
              </Tabs>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB 3 — Invoices (monthly rollup)
// ═══════════════════════════════════════════════════════════════════
function InvoicesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/invoices`);
      setRows(r.data.invoices || []);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 space-y-3">
      <div className="flex justify-between text-xs text-gray-500">
        <div>{rows.length} monthly invoice{rows.length !== 1 ? 's' : ''}</div>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="mfcwl-invoices-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Month</th>
                <th className="text-right px-3 py-2">Inspections</th>
                <th className="text-left px-3 py-2">Tiers used</th>
                <th className="text-right px-3 py-2">Total (INR)</th>
                <th className="text-left px-3 py-2">Latest</th>
                <th className="text-right px-3 py-2">PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">No billable inspections yet</td></tr>
              ) : rows.map(r => (
                <tr key={r.month} className="border-t">
                  <td className="px-3 py-2 font-mono">{r.month}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                  <td className="px-3 py-2 text-xs">{(r.tiers || []).join(', ')}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">₹ {Number(r.total_inr).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(r.latest)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" disabled title="PDF generation wired next iteration">
                      <FileText className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-gray-400 italic">
        PDF invoice auto-generation is scheduled for month-end. For now, row totals reflect every completed inspection's tier price.
      </p>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Activity & Notes tab — full chronological timeline + manual notes
// ═══════════════════════════════════════════════════════════════════
const ACTION_META = {
  'mfcwl.inspection.note':             { icon: MessageSquare, color: 'text-sky-600',    label: 'Note' },
  'mfcwl.inspection.assign':           { icon: UserPlus,      color: 'text-amber-600',  label: 'Mechanic assigned' },
  'mfcwl.inspection.status':           { icon: Activity,      color: 'text-indigo-600', label: 'Status changed' },
  'mfcwl.inspection.complete':         { icon: CheckCircle2,  color: 'text-emerald-600',label: 'Completed' },
  'mfcwl.admin.inspection.create':     { icon: Plus,          color: 'text-slate-600',  label: 'Created (admin)' },
  'mfcwl.partner.inspection.create':   { icon: Plus,          color: 'text-slate-600',  label: 'Received from MFCWL' },
  'mfcwl.webhook.dispatched':          { icon: Send,          color: 'text-blue-600',   label: 'Webhook → MFCWL (dispatched)' },
  'mfcwl.webhook.delivered':           { icon: CheckCircle,   color: 'text-emerald-600',label: 'Webhook delivered' },
  'mfcwl.webhook.failed':              { icon: AlertTriangle, color: 'text-rose-600',   label: 'Webhook failed' },
  'mfcwl.webhook.skipped':             { icon: Webhook,       color: 'text-slate-400',  label: 'Webhook skipped' },
  'mfcwl.notification.sent':           { icon: Bell,          color: 'text-emerald-600',label: 'Mechanic notified' },
  'mfcwl.notification.failed':         { icon: AlertTriangle, color: 'text-rose-600',   label: 'Notification failed' },
  'mfcwl.notification.skipped':        { icon: Bell,          color: 'text-slate-400',  label: 'Notification skipped' },
};

function _actionMeta(action) {
  return ACTION_META[action] || { icon: Activity, color: 'text-slate-500', label: action };
}

function ActivityNotesTab({ inspectionId, events, loading, onRefresh }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const trimmed = note.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/notes`, { note: trimmed });
      setNote('');
      toast.success('Note added');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="activity-notes-tab">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="h-4 w-4 text-sky-600" />
            <div className="text-sm font-medium text-slate-800">Add Note</div>
            <span className="text-[11px] text-slate-400 ml-auto">
              Captures who/when — surfaces in the timeline below.
            </span>
          </div>
          <textarea
            className="w-full min-h-[80px] rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:border-indigo-400"
            placeholder="E.g. Called customer at 2pm, rescheduled to 6pm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
            data-testid="mfcwl-note-input"
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-[11px] text-slate-400">{note.length}/2000</div>
            <Button size="sm" onClick={submit}
                    disabled={saving || !note.trim()}
                    data-testid="mfcwl-note-submit">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
              Add Note
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-600" />
              Activity Timeline
              <span className="text-[11px] font-normal text-slate-400">
                {events.length} event{events.length !== 1 ? 's' : ''}
              </span>
            </div>
            <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                    onClick={onRefresh} disabled={loading}
                    data-testid="activity-refresh">
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          {loading && !events.length ? (
            <div className="text-xs text-slate-400 italic">Loading…</div>
          ) : events.length === 0 ? (
            <div className="text-xs text-slate-400 italic">No activity yet.</div>
          ) : (
            <ul className="space-y-2.5">
              {events.map((e, i) => {
                const meta = _actionMeta(e.action);
                const Icon = meta.icon;
                const d = e.details || {};
                const actor = (e.actor_email || e.actor_id || e.actor_type || 'system');
                return (
                  <li key={e.id || i}
                      className="flex gap-3 border-l-2 border-slate-200 pl-3 py-1"
                      data-testid={`activity-event-${i}`}>
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-slate-800">{meta.label}</div>
                      {e.action === 'mfcwl.inspection.note' && d.note && (
                        <div className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5">{d.note}</div>
                      )}
                      {e.action === 'mfcwl.inspection.status' && d.status && (
                        <div className="text-xs text-slate-600 mt-0.5">New status: <span className="font-mono">{d.status}</span></div>
                      )}
                      {e.action === 'mfcwl.inspection.assign' && (
                        <div className="text-xs text-slate-600 mt-0.5">
                          Mechanic: <span className="font-medium">{d.mechanic_name || d.mechanic_id || '—'}</span>
                          {d.was_reassignment && <span className="ml-1 text-amber-600">(reassignment)</span>}
                        </div>
                      )}
                      {(e.action || '').startsWith('mfcwl.webhook') && (
                        <div className="text-xs text-slate-600 mt-0.5">
                          {d.event && <span>Event: <span className="font-mono">{d.event}</span></span>}
                          {d.http_status != null && <span> · HTTP {d.http_status}</span>}
                          {d.attempt != null && <span> · attempt {d.attempt}</span>}
                          {d.reason && <span className="text-slate-400"> · {d.reason}</span>}
                        </div>
                      )}
                      {(e.action || '').startsWith('mfcwl.notification') && (
                        <div className="text-xs text-slate-600 mt-0.5">
                          {d.channel && <span>via <span className="font-mono">{d.channel}</span></span>}
                          {d.kind && <span> · {d.kind}</span>}
                          {d.reason && <span className="text-slate-400"> · {d.reason}</span>}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-400 mt-1 font-mono">
                        {fmtDate(e.ts)} · {actor}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MFCWL Settings Dialog — webhook config + mechanic-notification config
// ═══════════════════════════════════════════════════════════════════
function MfcwlSettingsDialog({ open, onOpenChange }) {
  const [webhook, setWebhook] = useState(null);
  const [notif, setNotif] = useState(null);
  const [aiCfg, setAiCfg] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [w, n, a, d] = await Promise.all([
        axios.get(`${BASE}/webhook-config`),
        axios.get(`${BASE}/notification-settings`),
        axios.get(`${BASE}/ai-config`),
        axios.get(`${BASE}/webhook-deliveries?limit=20`),
      ]);
      setWebhook(w.data);
      setNotif(n.data);
      setAiCfg(a.data);
      setDeliveries(d.data?.deliveries || []);
    } catch (e) {
      toast.error('Failed to load MFCWL settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { loadAll(); setNewApiKey(''); } }, [open, loadAll]);

  const saveWebhook = async () => {
    setSaving(true);
    try {
      const body = {
        webhook_url: webhook.webhook_url || '',
        webhook_enabled: !!webhook.webhook_enabled,
        webhook_events: webhook.webhook_events || [],
      };
      if (newApiKey.trim()) body.webhook_api_key = newApiKey.trim();
      await axios.put(`${BASE}/webhook-config`, body);
      toast.success('Webhook settings saved');
      setNewApiKey('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveNotif = async () => {
    setSaving(true);
    try {
      await axios.put(`${BASE}/notification-settings`, {
        primary_channel:     notif.primary_channel,
        fallback_on_failure: !!notif.fallback_on_failure,
        enabled:             !!notif.enabled,
      });
      toast.success('Notification settings saved');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveAi = async () => {
    setSaving(true);
    try {
      await axios.put(`${BASE}/ai-config`, {
        analysis_url: aiCfg.analysis_url || '',
        status_url:   aiCfg.status_url || '',
        enabled:      !!aiCfg.enabled,
      });
      toast.success('AI Engine settings saved');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  const resetAiToDefaults = () => {
    setAiCfg({
      ...aiCfg,
      analysis_url: aiCfg.default_analysis_url,
      status_url:   aiCfg.default_status_url,
    });
  };

  const ALL_EVENTS = [
    'inspection.received',
    'inspection.assigned',
    'inspection.in_progress',
    'inspection.completed',
    'inspection.cancelled',
    'inspection.new',
    'inspection.report.ready',
  ];
  const toggleEvent = (ev) => {
    const current = new Set(webhook.webhook_events || []);
    if (current.has(ev)) current.delete(ev); else current.add(ev);
    setWebhook({ ...webhook, webhook_events: Array.from(current) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="mfcwl-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-indigo-600" /> MFCWL Settings
          </DialogTitle>
        </DialogHeader>

        {loading || !webhook || !notif || !aiCfg ? (
          <div className="py-10 text-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : (
          <Tabs defaultValue="webhook" className="w-full">
            <TabsList data-testid="settings-tabs">
              <TabsTrigger value="webhook" data-testid="settings-tab-webhook">
                <Webhook className="h-3.5 w-3.5 mr-1.5" /> Webhook
              </TabsTrigger>
              <TabsTrigger value="notif" data-testid="settings-tab-notif">
                <Bell className="h-3.5 w-3.5 mr-1.5" /> Mechanic Notifications
              </TabsTrigger>
              <TabsTrigger value="ai" data-testid="settings-tab-ai">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> AI Engine
              </TabsTrigger>
              <TabsTrigger value="carpm" data-testid="settings-tab-carpm">
                <Cpu className="h-3.5 w-3.5 mr-1.5" /> CARPM OBD
              </TabsTrigger>
              <TabsTrigger value="deliveries" data-testid="settings-tab-deliveries">
                <Send className="h-3.5 w-3.5 mr-1.5" /> Delivery Log
              </TabsTrigger>
            </TabsList>

            {/* ── Webhook ──────────────────────────────────────── */}
            <TabsContent value="webhook" className="space-y-4 pt-3">
              <div className="text-xs text-slate-500">
                Dormant by default. When enabled + URL + API key set, every inspection event
                is POSTed to MFCWL with headers <span className="font-mono">Authorization: Bearer &lt;key&gt;</span>,{' '}
                <span className="font-mono">X-WiseDrive-Signature</span>, and jittered retry.
              </div>

              <div>
                <Label className="text-xs">Webhook URL</Label>
                <Input
                  value={webhook.webhook_url || ''}
                  onChange={(e) => setWebhook({ ...webhook, webhook_url: e.target.value })}
                  placeholder="https://mfcwl.example.com/api/wisedrive/callback"
                  data-testid="webhook-url-input"
                />
              </div>

              <div>
                <Label className="text-xs">
                  API Key {webhook.webhook_api_key_set ? (
                    <span className="ml-1 text-emerald-600 text-[10px]">(set — leave blank to keep)</span>
                  ) : (
                    <span className="ml-1 text-amber-600 text-[10px]">(not set)</span>
                  )}
                </Label>
                <Input
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  placeholder={webhook.webhook_api_key_set ? '•••••••••••• (enter new key to rotate)' : 'Paste API key from MFCWL'}
                  data-testid="webhook-apikey-input"
                />
              </div>

              <div>
                <Label className="text-xs block mb-1.5">Events</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_EVENTS.map(ev => {
                    const on = (webhook.webhook_events || []).includes(ev);
                    return (
                      <button
                        key={ev}
                        type="button"
                        onClick={() => toggleEvent(ev)}
                        className={[
                          'px-2.5 py-1 rounded-full border text-[11px] font-mono',
                          on ? 'bg-indigo-600 text-white border-indigo-600'
                             : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400',
                        ].join(' ')}
                        data-testid={`webhook-event-${ev}`}
                      >
                        {ev}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="webhook-enabled"
                  checked={!!webhook.webhook_enabled}
                  onChange={(e) => setWebhook({ ...webhook, webhook_enabled: e.target.checked })}
                  data-testid="webhook-enabled-toggle"
                />
                <Label htmlFor="webhook-enabled" className="text-xs">Enable webhook dispatch</Label>
              </div>

              <DialogFooter>
                <Button onClick={saveWebhook} disabled={saving} data-testid="webhook-save">
                  {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                  Save webhook settings
                </Button>
              </DialogFooter>
            </TabsContent>

            {/* ── Mechanic Notifications ──────────────────────── */}
            <TabsContent value="notif" className="space-y-4 pt-3">
              <div className="text-xs text-slate-500">
                Controls how mechanics are notified when they're assigned an MFCWL inspection.
                Preference hierarchy: mechanic's own app-level preference &gt; settings below.
              </div>

              <div>
                <Label className="text-xs">Primary channel</Label>
                <Select value={notif.primary_channel}
                        onValueChange={(v) => setNotif({ ...notif, primary_channel: v })}>
                  <SelectTrigger data-testid="notif-primary-channel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="both">Both (WhatsApp + SMS)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notif-fallback"
                  checked={!!notif.fallback_on_failure}
                  onChange={(e) => setNotif({ ...notif, fallback_on_failure: e.target.checked })}
                  data-testid="notif-fallback-toggle"
                />
                <Label htmlFor="notif-fallback" className="text-xs">
                  Fall back to the other channel if the primary fails
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notif-enabled"
                  checked={!!notif.enabled}
                  onChange={(e) => setNotif({ ...notif, enabled: e.target.checked })}
                  data-testid="notif-enabled-toggle"
                />
                <Label htmlFor="notif-enabled" className="text-xs">Enable mechanic notifications</Label>
              </div>

              <DialogFooter>
                <Button onClick={saveNotif} disabled={saving} data-testid="notif-save">
                  {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                  Save notification settings
                </Button>
              </DialogFooter>
            </TabsContent>

            {/* ── AI Engine ───────────────────────────────────── */}
            <TabsContent value="ai" className="space-y-4 pt-3">
              <div className="text-xs text-slate-500">
                Configure the Auto-Inspect-Pro engine analysis service URLs.
                Resolution order at runtime is <b>DB &gt; env var &gt; default</b>.
                No authentication is required for now (network-trust); add an
                Authorization header in <span className="font-mono">ai_engine_client.py</span> when needed.
              </div>

              <div>
                <Label className="text-xs">Analysis URL (POST)</Label>
                <Input
                  value={aiCfg.analysis_url || ''}
                  onChange={(e) => setAiCfg({ ...aiCfg, analysis_url: e.target.value })}
                  placeholder={aiCfg.default_analysis_url}
                  data-testid="ai-analysis-url-input"
                  className="font-mono text-xs"
                />
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Default: <span className="font-mono">{aiCfg.default_analysis_url}</span>
                </div>
              </div>

              <div>
                <Label className="text-xs">Status URL (GET base — analysis_id is appended)</Label>
                <Input
                  value={aiCfg.status_url || ''}
                  onChange={(e) => setAiCfg({ ...aiCfg, status_url: e.target.value })}
                  placeholder={aiCfg.default_status_url}
                  data-testid="ai-status-url-input"
                  className="font-mono text-xs"
                />
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Default: <span className="font-mono">{aiCfg.default_status_url}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="ai-enabled"
                  checked={!!aiCfg.enabled}
                  onChange={(e) => setAiCfg({ ...aiCfg, enabled: e.target.checked })}
                  data-testid="ai-enabled-toggle"
                />
                <Label htmlFor="ai-enabled" className="text-xs">
                  Enable AI engine analysis on engine-video upload
                </Label>
              </div>

              <div className="text-[11px] text-slate-500">
                <b>Retry policy:</b> Submit retries up to 3 times (0s → 3s → 9s).
                Status polling 30s × 20 attempts (10 min). All steps audited
                in the inspection's <b>Activity &amp; Notes</b> tab.
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={resetAiToDefaults}
                        data-testid="ai-reset-defaults">
                  Reset to defaults
                </Button>
                <Button onClick={saveAi} disabled={saving} data-testid="ai-save">
                  {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                  Save AI settings
                </Button>
              </DialogFooter>
            </TabsContent>

            {/* ── CARPM OBD Webhook ────────────────────────────── */}
            <TabsContent value="carpm" className="space-y-3 pt-3" data-testid="settings-carpm-content">
              <CarpmWebhookPanel />
            </TabsContent>


            {/* ── Deliveries ──────────────────────────────────── */}
            <TabsContent value="deliveries" className="space-y-3 pt-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Last 20 webhook delivery attempts.
                </div>
                <Button size="sm" variant="ghost" onClick={loadAll} className="h-7 text-[11px]">
                  <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                </Button>
              </div>
              {deliveries.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No deliveries yet.</div>
              ) : (
                <div className="border rounded overflow-auto max-h-[50vh]">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="text-left px-2 py-1.5">When</th>
                        <th className="text-left px-2 py-1.5">Event</th>
                        <th className="text-left px-2 py-1.5">Inspection</th>
                        <th className="text-left px-2 py-1.5">Status</th>
                        <th className="text-left px-2 py-1.5">HTTP</th>
                        <th className="text-left px-2 py-1.5">Attempt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveries.map((d, i) => (
                        <tr key={d.id || i} className="border-t" data-testid={`delivery-row-${i}`}>
                          <td className="px-2 py-1.5 font-mono text-[10px]">{fmtDate(d.ts)}</td>
                          <td className="px-2 py-1.5 font-mono">{d.event}</td>
                          <td className="px-2 py-1.5 font-mono text-[10px]">{d.inspection_id}</td>
                          <td className="px-2 py-1.5">
                            <Badge className={
                              d.status === 'DELIVERED' ? 'bg-emerald-100 text-emerald-700'
                              : d.status === 'FAILED'  ? 'bg-rose-100 text-rose-700'
                              : 'bg-amber-100 text-amber-700'
                            }>{d.status}</Badge>
                          </td>
                          <td className="px-2 py-1.5">{d.http_status ?? '—'}</td>
                          <td className="px-2 py-1.5">{d.attempt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Auto-Inspect-Pro AI engine analysis — structured + raw JSON viewer
// ═══════════════════════════════════════════════════════════════════
function CombinedVerdictCard({ doc }) {
  const cv = doc?.combined_verdict;
  if (!cv) return null;

  const status = cv.status || 'INSUFFICIENT_DATA';
  const TONE = {
    HEALTHY:           { ring: 'ring-emerald-300', bar: 'bg-emerald-500', pill: 'bg-emerald-100 text-emerald-700', icon: 'text-emerald-600' },
    ATTENTION:         { ring: 'ring-amber-300',   bar: 'bg-amber-500',   pill: 'bg-amber-100 text-amber-700',     icon: 'text-amber-600' },
    CRITICAL:          { ring: 'ring-rose-300',    bar: 'bg-rose-500',    pill: 'bg-rose-100 text-rose-700',       icon: 'text-rose-600' },
    INSUFFICIENT_DATA: { ring: 'ring-slate-200',   bar: 'bg-slate-300',   pill: 'bg-slate-100 text-slate-600',     icon: 'text-slate-500' },
  };
  const t = TONE[status] || TONE.INSUFFICIENT_DATA;
  const score = cv.score;

  const SubBadge = ({ available, status: sStatus, children }) => {
    if (!available) return <Badge className="bg-slate-100 text-slate-500">N/A</Badge>;
    const tt = TONE[sStatus] || TONE.INSUFFICIENT_DATA;
    return <Badge className={tt.pill}>{children}</Badge>;
  };

  return (
    <Card data-testid="combined-verdict-card" className={`ring-1 ${t.ring}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className={`h-4 w-4 ${t.icon}`} />
            <div className="text-sm font-semibold text-slate-800">Combined Verdict</div>
            <Badge className={t.pill} data-testid="combined-verdict-status">{cv.label || status}</Badge>
          </div>
          {score != null && (
            <div className="text-right">
              <div className="text-[11px] uppercase text-slate-500">Overall Score</div>
              <div className="text-xl font-bold text-slate-800" data-testid="combined-verdict-score">
                {score}<span className="text-sm text-slate-400">/100</span>
              </div>
            </div>
          )}
        </div>

        {score != null && (
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-2 ${t.bar} transition-all`} style={{ width: `${Math.max(2, Math.min(100, score))}%` }} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="border rounded-md p-2.5 bg-slate-50">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase text-slate-500 font-semibold">Engine Sound</div>
              <SubBadge available={cv.engine?.available} status={cv.engine?.status}>
                {cv.engine?.available ? (cv.engine?.status || '—') : 'PENDING'}
              </SubBadge>
            </div>
            {cv.engine?.available ? (
              <div className="mt-1.5 text-xs text-slate-600 space-x-2">
                <span className="text-rose-700 font-medium">{cv.engine.critical} crit</span>
                <span className="text-amber-700 font-medium">{cv.engine.elevated} elev</span>
                <span className="text-emerald-700 font-medium">{cv.engine.normal} norm</span>
                {cv.engine.score != null && <span className="text-slate-500">· score {cv.engine.score}</span>}
              </div>
            ) : (
              <div className="mt-1.5 text-xs text-slate-400">Awaiting engine analysis</div>
            )}
          </div>

          <div className="border rounded-md p-2.5 bg-slate-50">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase text-slate-500 font-semibold">OBD Scan</div>
              <SubBadge available={cv.obd?.available} status={cv.obd?.status}>
                {cv.obd?.available ? (cv.obd?.status || '—') : 'PENDING'}
              </SubBadge>
            </div>
            {cv.obd?.available ? (
              <div className="mt-1.5 text-xs text-slate-600 space-x-2">
                <span className="text-rose-700 font-medium">{cv.obd.critical_count} crit</span>
                <span className="text-amber-700 font-medium">{cv.obd.warning_count} warn</span>
                <span className="text-slate-500">{cv.obd.info_count} info</span>
                {cv.obd.ai_verdict && <span className="text-slate-500">· {cv.obd.ai_verdict}</span>}
              </div>
            ) : (
              <div className="mt-1.5 text-xs text-slate-400">Awaiting OBD scan</div>
            )}
          </div>
        </div>

        {Array.isArray(cv.reasons) && cv.reasons.length > 0 && (
          <div className="border-t pt-2.5">
            <div className="text-[11px] uppercase text-slate-500 mb-1">Why this verdict</div>
            <ul className="text-xs text-slate-700 space-y-0.5 list-disc list-inside" data-testid="combined-verdict-reasons">
              {cv.reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AutoInspectProCard({ doc }) {
  const [showRaw, setShowRaw] = useState(false);
  // doc may be (a) the raw inspection (ai_engine at top level), or
  //          (b) the combined report dict where the inspection is nested.
  const ai = doc?.ai_engine || doc?.inspection?.ai_engine;
  if (!ai || !ai.status) return null;

  const status = (ai.status || '').toLowerCase();
  const resp = ai.response || ai.last_response || {};
  const cls = resp?.sound_classification || resp?.classification || null;
  const sum = cls?.summary || null;

  // Real response shape: summary has sound_health_score + counts; sound_types[] has per-sound rows.
  const score      = sum?.sound_health_score ?? null;
  const critical   = sum?.critical_count ?? 0;
  const elevated   = sum?.elevated_count ?? 0;
  const normal     = sum?.normal_count ?? 0;
  const total      = sum?.total_types_analyzed ?? sum?.detected_count ?? 0;
  const overallDb  = sum?.overall_db ?? null;
  const verdict    = score == null ? null
                     : (critical > 0 ? 'Critical'
                       : (elevated > 0 ? 'Needs Attention' : 'Healthy'));
  const verdictTone = critical > 0 ? 'text-rose-700'
                     : (elevated > 0 ? 'text-amber-700' : 'text-emerald-700');
  const subTypes = Array.isArray(cls?.sound_types) ? cls.sound_types : [];

  const COLOR = {
    completed:   'bg-emerald-100 text-emerald-700',
    error:       'bg-rose-100 text-rose-700',
    submit_error:'bg-rose-100 text-rose-700',
    submitted:   'bg-blue-100 text-blue-700',
    queued:      'bg-amber-100 text-amber-700',
    in_progress: 'bg-indigo-100 text-indigo-700',
    timeout:     'bg-gray-200 text-gray-600',
  };
  const cardColor = COLOR[status] || 'bg-slate-100 text-slate-700';

  return (
    <Card data-testid="auto-inspect-pro-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-indigo-600" />
              <div className="text-sm font-semibold text-slate-800">Auto-Inspect-Pro · AI Engine Analysis</div>
              <Badge className={cardColor} data-testid="aip-status-badge">{status.toUpperCase()}</Badge>
            </div>
            {ai.analysis_id && (
              <div className="mt-1 text-[11px] text-slate-500 font-mono">
                analysis_id: {ai.analysis_id}
              </div>
            )}
            {ai.message && (
              <div className="mt-1 text-xs text-slate-600">{ai.message}</div>
            )}
          </div>
          {(ai.poll_count != null || ai.last_polled_at) && (
            <div className="text-[11px] text-slate-400 text-right">
              {ai.poll_count != null && <div>polls: {ai.poll_count}</div>}
              {ai.last_polled_at && <div>last: {fmtDate(ai.last_polled_at)}</div>}
            </div>
          )}
        </div>

        {/* Failure panel — shown when AI submit failed, the upstream service
            reported error, the polling timed out, or the run was marked
            FAILED. Surfaces the underlying error so admin can decide whether
            to re-upload or re-analyse. */}
        {['error', 'submit_error', 'timeout', 'failed'].includes(status) && (
          <div className="border border-rose-200 bg-rose-50 rounded-md p-3 space-y-1"
               data-testid="aip-failure-panel">
            <div className="flex items-center gap-1.5 text-rose-800 font-semibold text-sm">
              <AlertTriangle className="h-4 w-4" />
              {status === 'submit_error' && 'AI submission failed'}
              {status === 'error'        && 'AI engine reported an error'}
              {status === 'timeout'      && 'AI analysis timed out'}
              {status === 'failed'       && 'Analysis failed'}
            </div>
            {(ai.last_error || ai.message) && (
              <div className="text-xs text-rose-700 break-words">
                {ai.last_error || ai.message}
              </div>
            )}
            <div className="text-[11px] text-rose-600/80">
              Try re-uploading the audio/video, or click <b>Re-analyse</b> to retry on the existing file.
            </div>
          </div>
        )}

        {/* Structured panel — driven by sound_classification.summary */}
        {status === 'completed' && sum && (
          <div className="space-y-3">
            {/* Top KPI strip */}
            <div className="border rounded-md p-3 bg-slate-50">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-[11px] uppercase text-slate-500">Verdict</div>
                  <div className={`font-semibold ${verdictTone}`} data-testid="aip-verdict">{verdict || '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-slate-500">Health Score</div>
                  <div className="font-semibold text-slate-800" data-testid="aip-health-score">
                    {score != null ? `${score}/100` : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-slate-500">Sounds Analyzed</div>
                  <div className="font-semibold text-slate-800">{total}</div>
                </div>
                {overallDb != null && (
                  <div>
                    <div className="text-[11px] uppercase text-slate-500">Overall dB</div>
                    <div className="font-semibold text-slate-800">{overallDb}</div>
                  </div>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge className="bg-rose-100 text-rose-700">{critical} critical</Badge>
                <Badge className="bg-amber-100 text-amber-700">{elevated} elevated</Badge>
                <Badge className="bg-emerald-100 text-emerald-700">{normal} normal</Badge>
              </div>
            </div>

            {/* Engine validation */}
            <ValidationCard validation={resp?.validation} />

            {/* Frequency histogram */}
            <HistogramCard hist={resp?.histogram_data} />

            {/* Sound type detailed table */}
            <SoundTypesTable rows={subTypes} />

            {/* Noise cancellation */}
            <NoiseCancelCard nc={resp?.noise_cancellation} />
          </div>
        )}

        {/* Raw JSON toggle — always available for debugging */}
        <div className="flex items-center justify-between">
          <Button
            size="sm" variant="ghost"
            onClick={() => setShowRaw((v) => !v)}
            className="h-7 text-[11px]"
            data-testid="aip-toggle-raw">
            <Code className="h-3 w-3 mr-1" />
            {showRaw ? 'Hide' : 'View'} raw JSON
          </Button>
          {ai.last_error && (
            <span className="text-[11px] text-rose-600 font-mono truncate max-w-[60%]"
                  title={ai.last_error}>
              {ai.last_error.slice(0, 80)}
            </span>
          )}
        </div>
        {showRaw && (
          <pre
            className="text-[11px] font-mono bg-slate-900 text-slate-100 p-3 rounded-md overflow-auto max-h-[300px]"
            data-testid="aip-raw-json">
            {JSON.stringify(ai, null, 2)}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

// ── Auto-Inspect-Pro sub-components ──────────────────────────────────
function ValidationCard({ validation }) {
  if (!validation) return null;
  const v = validation;
  const isEngine = !!v.is_engine;
  const conf     = v.confidence;
  const f        = v.features || {};
  return (
    <div className="border rounded-md p-3 bg-white" data-testid="aip-validation">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Audio Validation</div>
        <Badge className={isEngine ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
          {isEngine ? 'Engine confirmed' : 'Not engine sound'}
        </Badge>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        {conf != null && (
          <div>
            <div className="text-slate-500">Confidence</div>
            <div className="font-semibold text-slate-800">{conf}%</div>
          </div>
        )}
        {f.estimated_rpm != null && (
          <div>
            <div className="text-slate-500">Estimated RPM</div>
            <div className="font-semibold text-slate-800">{Math.round(f.estimated_rpm)}</div>
          </div>
        )}
        {f.spectral_centroid != null && (
          <div>
            <div className="text-slate-500">Spectral Centroid</div>
            <div className="font-semibold text-slate-800">{Math.round(f.spectral_centroid)} Hz</div>
          </div>
        )}
        {f.rms_energy != null && (
          <div>
            <div className="text-slate-500">RMS Energy</div>
            <div className="font-semibold text-slate-800">{f.rms_energy.toFixed(3)}</div>
          </div>
        )}
      </div>
      {v.reason && <div className="mt-2 text-[11px] text-slate-500 italic">{v.reason}</div>}
    </div>
  );
}

function HistogramCard({ hist }) {
  // hist may be {histogram: [...], bin_edges: [...], peak_freq, ...} or null
  const data = Array.isArray(hist?.histogram) ? hist.histogram : null;
  if (!data || data.length === 0) return null;
  // Each item may be {freq, magnitude} or {bin_start, bin_end, count} etc.
  // Render a generic bar chart of (idx → magnitude/count).
  const values = data.map((d) => {
    if (typeof d === 'number') return d;
    return d.magnitude ?? d.count ?? d.value ?? d.amplitude ?? 0;
  });
  const max = Math.max(...values, 1);
  return (
    <div className="border rounded-md p-3 bg-white" data-testid="aip-histogram">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Frequency Histogram</div>
        <div className="text-[11px] text-slate-500">{data.length} bins</div>
      </div>
      <div className="flex items-end gap-[1px] h-24">
        {values.map((v, i) => (
          <div key={i}
               className="flex-1 bg-indigo-400 hover:bg-indigo-600 transition-colors"
               style={{ height: `${(v / max) * 100}%`, minHeight: '1px' }}
               title={`bin ${i}: ${v.toFixed(2)}`} />
        ))}
      </div>
      {hist.peak_freq != null && (
        <div className="mt-2 text-[11px] text-slate-500">Peak frequency: <span className="font-semibold text-slate-700">{Math.round(hist.peak_freq)} Hz</span></div>
      )}
    </div>
  );
}

function SoundTypesTable({ rows }) {
  const [expanded, setExpanded] = useState(null);
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const tone = (status, severity) => {
    const st = (status || '').toLowerCase();
    const sev = (severity || '').toLowerCase();
    if (st === 'critical' || sev === 'severe' || sev === 'critical')
      return { row: 'bg-rose-50',    badge: 'bg-rose-100 text-rose-700' };
    if (st === 'elevated' || sev === 'mild' || sev === 'moderate')
      return { row: 'bg-amber-50',   badge: 'bg-amber-100 text-amber-700' };
    if (st === 'normal')
      return { row: 'bg-emerald-50/40', badge: 'bg-emerald-100 text-emerald-700' };
    return   { row: 'bg-slate-50',   badge: 'bg-slate-100 text-slate-600' };
  };

  return (
    <div className="border rounded-md bg-white overflow-hidden" data-testid="aip-sound-types-table">
      <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Sound Type Analysis</div>
        <div className="text-[11px] text-slate-500">{rows.length} types</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Sound</th>
              <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Frequency</th>
              <th className="text-right px-3 py-2 font-medium">Measured</th>
              <th className="text-right px-3 py-2 font-medium hidden md:table-cell">Ideal</th>
              <th className="text-right px-3 py-2 font-medium">Deviation</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="w-6"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const t = tone(r.status, r.severity);
              const isOpen = expanded === i;
              const dev = r.deviation_pct;
              const devColor = dev > 50 ? 'text-rose-700' : dev > 20 ? 'text-amber-700' : 'text-slate-600';
              return (
                <React.Fragment key={i}>
                  <tr className={`${t.row} border-t cursor-pointer hover:bg-slate-100/60`}
                      onClick={() => setExpanded(isOpen ? null : i)}>
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {r.name || '—'}
                      {r.category && <span className="ml-1 text-[10px] text-slate-400">· {r.category}</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 hidden sm:table-cell">{r.freq_range || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.measured_db != null ? `${r.measured_db} dB` : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums hidden md:table-cell text-slate-500">{r.ideal_db != null ? `${r.ideal_db} dB` : '—'}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-medium ${devColor}`}>
                      {dev != null ? `${dev > 0 ? '+' : ''}${dev.toFixed(1)}%` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`${t.badge} text-[10px]`}>{(r.status || '—').toUpperCase()}</Badge>
                    </td>
                    <td className="px-2 text-slate-400">{isOpen ? '−' : '+'}</td>
                  </tr>
                  {isOpen && (
                    <tr className={`${t.row} border-t`}>
                      <td colSpan={7} className="px-3 py-3">
                        {r.description && <div className="text-slate-700 mb-2"><span className="font-semibold">Description:</span> {r.description}</div>}
                        {r.normal_cause && <div className="text-emerald-700 mb-1"><span className="font-semibold">Normal cause:</span> {r.normal_cause}</div>}
                        {r.abnormal_cause && <div className="text-rose-700"><span className="font-semibold">Possible cause:</span> {r.abnormal_cause}</div>}
                        {(r.character || r.spectral_flatness != null) && (
                          <div className="mt-2 text-[11px] text-slate-500">
                            {r.character && <span>Character: <span className="text-slate-700">{r.character}</span></span>}
                            {r.spectral_flatness != null && <span className="ml-3">Spectral flatness: <span className="text-slate-700">{r.spectral_flatness.toFixed(2)}</span></span>}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NoiseCancelCard({ nc }) {
  if (!nc) return null;
  const ok = !!nc.success;
  const reduction = nc.noise_reduction_pct;
  return (
    <div className="border rounded-md p-3 bg-white" data-testid="aip-noise">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Noise Cancellation</div>
        <Badge className={ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
          {ok ? 'Applied' : 'Failed'}
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        {nc.original_duration != null && (
          <div>
            <div className="text-slate-500">Original</div>
            <div className="font-semibold text-slate-800">{nc.original_duration.toFixed(1)}s</div>
          </div>
        )}
        {nc.clean_duration != null && (
          <div>
            <div className="text-slate-500">Clean</div>
            <div className="font-semibold text-slate-800">{nc.clean_duration.toFixed(1)}s</div>
          </div>
        )}
        {reduction != null && (
          <div>
            <div className="text-slate-500">Reduction</div>
            <div className={`font-semibold ${reduction >= 0 ? 'text-emerald-700' : 'text-slate-700'}`}>
              {reduction >= 0 ? `${reduction.toFixed(1)}%` : '—'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── OBD PDF preview card (OBD2 tab) ──────────────────────────────────
function ObdPdfCard({ doc, inspectionId, onRefresh }) {
  const meta = doc?.inspection?.obd_pdf || doc?.obd_pdf;
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Fetch the PDF as a blob so we can preview it in an <iframe> (iframes
  // can't send Authorization headers; using axios + blob URL keeps the
  // admin auth path intact).
  useEffect(() => {
    let cancelled = false;
    let url = null;
    if (!meta || !meta.uploaded_at || !inspectionId) {
      setBlobUrl(null);
      return;
    }
    // Reset the preview while we refetch (replaces show "Loading PDF…"
    // instead of the old blob).
    setBlobUrl(null);
    setErr(null);
    (async () => {
      try {
        const r = await axios.get(
          `${BASE}/inspections/${inspectionId}/obd-pdf`,
          { responseType: 'blob' },
        );
        url = URL.createObjectURL(r.data);
        if (!cancelled) setBlobUrl(url);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.status === 404 ? 'PDF not found on server' : 'Failed to load PDF');
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [meta?.uploaded_at, inspectionId, meta]);

  // Upload handler — called when admin picks a PDF from disk. If a PDF
  // already exists on the inspection we ask for explicit confirmation
  // before overwriting (matches the user's product requirement: "prompt
  // and overwrite if yes").
  const handleUpload = async (file) => {
    if (!file) return;
    if (file.type && file.type !== 'application/pdf' &&
        !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    if (meta && meta.uploaded_at) {
      const existingName = meta.filename || 'obd.pdf';
      const ok = window.confirm(
        `An OBD PDF is already attached to this inspection:\n\n` +
        `  ${existingName}\n` +
        `  uploaded ${meta.uploaded_at}\n\n` +
        `Replace it with "${file.name}"?`
      );
      if (!ok) return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('obd_pdf', file);
      await axios.post(`${BASE}/inspections/${inspectionId}/upload`, fd);
      toast.success(meta?.uploaded_at ? 'OBD PDF replaced' : 'OBD PDF uploaded');
      if (typeof onRefresh === 'function') onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'OBD PDF upload failed');
    } finally {
      setUploading(false);
    }
  };

  const hasPdf = !!(meta && meta.uploaded_at);

  // Human-friendly size: prefer KB; if < 1 KB, show bytes.
  const sizeStr = (!hasPdf || meta.size == null)
    ? null
    : meta.size < 1024
      ? `${meta.size} B`
      : `${(meta.size / 1024).toFixed(1)} KB`;

  return (
    <Card data-testid="obd-pdf-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText className="h-4 w-4 text-rose-600" />
            <div className="text-sm font-semibold text-slate-800">OBD2 Report PDF</div>
            {hasPdf ? (
              <>
                <Badge className="bg-emerald-100 text-emerald-700">Uploaded</Badge>
                {meta.source === 'carpm' && (
                  <Badge className="bg-indigo-100 text-indigo-700" data-testid="obd-pdf-source-carpm">
                    from CARPM
                  </Badge>
                )}
                {meta.source === 'admin_upload' && (
                  <Badge className="bg-slate-100 text-slate-700">admin upload</Badge>
                )}
                {meta.source === 'partner_upload' && (
                  <Badge className="bg-slate-100 text-slate-700">partner upload</Badge>
                )}
              </>
            ) : (
              <Badge className="bg-slate-100 text-slate-500">No PDF yet</Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            {hasPdf && (
              <div className="text-[11px] text-slate-500 text-right">
                <div className="truncate max-w-[260px]" title={meta.filename}>{meta.filename || 'obd.pdf'}</div>
                {sizeStr && <div>{sizeStr}</div>}
                {meta.uploaded_at && <div>uploaded: {fmtDate(meta.uploaded_at)}</div>}
              </div>
            )}
            <label className="cursor-pointer">
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  handleUpload(f);
                }}
                data-testid="obd-pdf-upload-input"
                disabled={uploading}
              />
              <Button
                size="sm"
                variant={hasPdf ? 'outline' : 'default'}
                disabled={uploading}
                asChild
                data-testid="obd-pdf-upload-btn"
                className={!hasPdf ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}>
                <span>
                  {uploading
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Uploading…</>
                    : <><Upload className="h-3.5 w-3.5 mr-1" /> {hasPdf ? 'Replace PDF' : 'Upload PDF'}</>}
                </span>
              </Button>
            </label>
          </div>
        </div>
        {!hasPdf ? (
          <div className="border border-dashed rounded-md p-6 text-center text-sm text-slate-500"
               data-testid="obd-pdf-empty-state">
            No OBD PDF attached yet. Click <b>Upload PDF</b> to attach a CaRPM/OBD scanner report.
          </div>
        ) : (
          <>
            <div className="border rounded-md overflow-hidden bg-slate-100" style={{ height: 600 }}>
              {err ? (
                <div className="h-full flex items-center justify-center text-sm text-rose-700">{err}</div>
              ) : !blobUrl ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading PDF…
                </div>
              ) : (
                <iframe
                  src={blobUrl}
                  title="OBD PDF Preview"
                  className="w-full h-full"
                  data-testid="obd-pdf-iframe"
                />
              )}
            </div>
            <div className="flex justify-end">
              <Button
                asChild={!!blobUrl}
                size="sm"
                variant="outline"
                data-testid="obd-pdf-download"
                disabled={!blobUrl}>
                {blobUrl ? (
                  <a href={blobUrl} download={meta.filename || 'obd.pdf'}>
                    <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
                  </a>
                ) : (
                  <span><Download className="h-3.5 w-3.5 mr-1" /> Download PDF</span>
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}


// ── Send-to-MFCWL card ──────────────────────────────────────────────
// Manual push of the latest OBD JSON (CARPM-sourced or PDF-parsed) to
// MFCWL's callback URL. Auto-dispatch runs on every OBD ingress so this
// button is for re-sends after config changes or to force a fresh send.
// Also lists the dispatch history (last 10) with status + timestamps.
function SendToMfcwlCard({ inspectionId, onRefresh }) {
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!inspectionId) return;
    setLoadingHist(true);
    try {
      const r = await axios.get(
        `${BASE}/inspections/${inspectionId}/mfcwl-dispatches?limit=10`,
      );
      setHistory(r.data?.dispatches || []);
    } catch (e) {
      // Silent — endpoint is admin-only and we don't want to flash an
      // error on every drawer open. Surface in DevTools.
      console.warn('mfcwl-dispatches fetch failed', e);
    } finally {
      setLoadingHist(false);
    }
  }, [inspectionId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(
        `${BASE}/inspections/${inspectionId}/send-to-mfcwl`,
      );
      const status = r.data?.status;
      if (status === 'sent') {
        toast.success(`Sent to MFCWL (HTTP ${r.data?.http_status})`);
      } else if (status === 'staged_no_url') {
        toast.message('Payload staged', {
          description: 'MFCWL_OBD_CALLBACK_URL not configured. Set it in env to enable outbound delivery.',
        });
      } else if (status === 'skipped_duplicate') {
        toast.message('No change since last send', {
          description: 'Content hash unchanged — MFCWL already has the latest payload.',
        });
      } else if (status === 'failed') {
        toast.error(`Dispatch failed: HTTP ${r.data?.http_status} ${r.data?.body || ''}`);
      } else {
        toast.success('Dispatch completed');
      }
      loadHistory();
      if (typeof onRefresh === 'function') onRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (detail === 'no-obd-data-available') {
        toast.error('No OBD data to send — upload a PDF or wait for CARPM JSON.');
      } else {
        toast.error(`Send failed: ${detail}`);
      }
    } finally {
      setSending(false);
    }
  };

  const STATUS_BADGE = {
    sent:               'bg-emerald-100 text-emerald-700',
    pending:            'bg-amber-100  text-amber-700',
    failed:             'bg-rose-100   text-rose-700',
    skipped_duplicate:  'bg-slate-100  text-slate-600',
  };

  return (
    <Card data-testid="send-to-mfcwl-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-indigo-600" />
            <div className="text-sm font-semibold text-slate-800">
              Send OBD JSON to MFCWL
            </div>
          </div>
          <Button
            size="sm"
            onClick={send}
            disabled={sending}
            data-testid="send-to-mfcwl-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {sending
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Sending…</>
              : <><Upload className="h-3.5 w-3.5 mr-1" /> Send to MFCWL</>}
          </Button>
        </div>
        <div className="text-[12px] text-slate-500">
          Auto-dispatch fires on every CaRPM webhook or PDF upload.
          Use this button to manually re-send the latest payload —
          content-hash idempotent, so re-sends are safe.
        </div>

        {/* Dispatch history */}
        <div className="border-t pt-3 space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-600">Recent dispatches</div>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                    onClick={loadHistory} disabled={loadingHist}
                    data-testid="refresh-mfcwl-history">
              {loadingHist ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
          {history.length === 0 ? (
            <div className="text-[11px] text-slate-400 italic">No dispatches yet.</div>
          ) : (
            <div className="space-y-1 max-h-[180px] overflow-y-auto">
              {history.map(h => (
                <div key={h.id} className="flex items-center gap-2 text-[11px]
                                            text-slate-600 border rounded px-2 py-1"
                     data-testid="mfcwl-dispatch-row">
                  <Badge className={`${STATUS_BADGE[h.status] || 'bg-gray-100 text-gray-600'} text-[10px]`}>
                    {h.status}
                  </Badge>
                  <span className="font-mono">{(h.source || '-').slice(0, 18)}</span>
                  <span className="text-slate-400">·</span>
                  <span>{fmtDate(h.created_at)}</span>
                  {h.response?.status != null && (
                    <span className="text-slate-400">· HTTP {h.response.status}</span>
                  )}
                  {h.last_error && (
                    <span className="text-rose-600 truncate" title={h.last_error}>
                      · {h.last_error.slice(0, 60)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


// ── CARPM OBD report (rendered on OBD2 tab) ──────────────────────────
// Renders raw_json received from CARPM. Storage shape lives in
// `b2b_carpm_obd_reports`; the report builder injects it into the
// existing report.json payload as `carpm_obd`.
function CarpmObdReport({ doc }) {
  const cp = doc?.carpm_obd;
  const [expanded, setExpanded] = useState(null);
  if (!cp || !cp.raw_json) return null;
  const j = cp.raw_json;

  const dtcs = Array.isArray(j.code_details) ? j.code_details : [];
  // Normalise faulty_modules: CARPM sometimes sends ["Engine","BCM"] (strings),
  // sometimes [{module:"Engine", dtcs:[…]}] (objects). Coerce both to strings
  // so the render path never sees a raw object (which would crash React).
  const toModuleName = (m) => {
    if (!m) return null;
    if (typeof m === 'string') return m;
    if (typeof m === 'object') return m.module || m.name || m.module_name || null;
    return String(m);
  };
  const faulty   = Array.isArray(j.faulty_modules)
    ? j.faulty_modules.map(toModuleName).filter(Boolean)
    : [];
  const nonFault = Array.isArray(j.non_faulty_modules)
    ? j.non_faulty_modules.map(toModuleName).filter(Boolean)
    : [];

  // DTC severity inferred from code prefix:
  //   P = Powertrain, U = Network/Communication → red
  //   C = Chassis → amber
  //   B = Body → slate
  // (CARPM doesn't send severity yet; this is a transparent local heuristic.)
  const dtcTone = (code) => {
    const p = (code || '').toUpperCase().charAt(0);
    if (p === 'P' || p === 'U') return { row: 'bg-rose-50',    badge: 'bg-rose-100 text-rose-700' };
    if (p === 'C')               return { row: 'bg-amber-50',   badge: 'bg-amber-100 text-amber-700' };
    if (p === 'B')               return { row: 'bg-slate-50',   badge: 'bg-slate-100 text-slate-700' };
    return                              { row: 'bg-white',     badge: 'bg-slate-100 text-slate-600' };
  };

  return (
    <Card data-testid="carpm-obd-card">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-indigo-600" />
            <div className="text-sm font-semibold text-slate-800">OBD2 Scan Report</div>
            <Badge className={cp.source === 'pdf'
                ? 'bg-amber-100 text-amber-800'
                : 'bg-indigo-100 text-indigo-700'}
                   data-testid="carpm-source">
              {cp.source === 'pdf' ? 'Parsed from PDF' : 'CARPM'}
            </Badge>
            {cp.match_status === 'unmatched' && (
              <Badge className="bg-amber-100 text-amber-700">Unmatched</Badge>
            )}
          </div>
          <div className="text-[11px] text-slate-500 text-right">
            {cp.received_at && <div>received: {fmtDate(cp.received_at)}</div>}
            {j.report_url && (
              <a href={j.report_url} target="_blank" rel="noreferrer"
                 className="text-indigo-600 hover:underline" data-testid="carpm-source-link">
                Source report ↗
              </a>
            )}
          </div>
        </div>

        {/* Vehicle / scan summary KPIs */}
        <div className="border rounded-md p-3 bg-slate-50 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-[11px] uppercase text-slate-500">License Plate</div>
            <div className="font-mono font-semibold text-slate-800" data-testid="carpm-license-plate">
              {j.license_plate || '—'}
            </div>
            {j.original_license_plate && j.original_license_plate !== j.license_plate && (
              <div className="text-[10px] text-slate-400 font-mono mt-0.5"
                   title="Plate printed on the uploaded PDF">
                pdf: {j.original_license_plate}
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">VIN</div>
            <div className="font-mono text-xs text-slate-700 break-all">{j.vin || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">Make</div>
            <div className="font-semibold text-slate-800">{j.car_company || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">MIL</div>
            <Badge className={j.mil_status ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}>
              {j.mil_status ? 'ON' : 'OFF'}
            </Badge>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">Mechanic</div>
            <div className="text-xs text-slate-700 truncate" title={j.mechanic_email}>{j.mechanic_name || '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">Battery</div>
            <div className="font-semibold text-slate-800">{j.battery_voltage != null ? `${j.battery_voltage} V` : '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">Scan Time</div>
            <div className="text-xs text-slate-700">{j.time ? fmtDate(j.time) : '—'}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-500">Scan Result</div>
            <div className="text-xs text-slate-700">{j.scan_ended || '—'}</div>
          </div>
        </div>

        {/* Module status — chips */}
        <div className="border rounded-md p-3 bg-white">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Modules</div>
          <div className="flex flex-wrap gap-1.5" data-testid="carpm-modules">
            {faulty.length === 0 && nonFault.length === 0 && (
              <span className="text-xs text-slate-400">No module info reported.</span>
            )}
            {faulty.map((m, i) => (
              <Badge key={`f${i}`} className="bg-rose-100 text-rose-700">{m} ✕</Badge>
            ))}
            {nonFault.map((m, i) => (
              <Badge key={`n${i}`} variant="outline" className="border-emerald-200 text-emerald-700">{m} ✓</Badge>
            ))}
          </div>
        </div>

        {/* DTC table */}
        {dtcs.length > 0 && (
          <div className="border rounded-md bg-white overflow-hidden" data-testid="carpm-dtc-table">
            <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Diagnostic Trouble Codes</div>
              <div className="text-[11px] text-slate-500">{dtcs.length} codes</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">DTC</th>
                    <th className="text-left px-3 py-2 font-medium">Module</th>
                    <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Status</th>
                    <th className="text-left px-3 py-2 font-medium">Meaning</th>
                    <th className="w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {dtcs.map((d, i) => {
                    const t = dtcTone(d.dtc);
                    const isOpen = expanded === i;
                    return (
                      <React.Fragment key={i}>
                        <tr className={`${t.row} border-t cursor-pointer hover:bg-slate-100/60`}
                            onClick={() => setExpanded(isOpen ? null : i)}>
                          <td className="px-3 py-2 font-mono font-semibold text-slate-800">{d.dtc || '—'}</td>
                          <td className="px-3 py-2 text-slate-700">{d.module || '—'}</td>
                          <td className="px-3 py-2 hidden sm:table-cell">
                            <Badge className={`${t.badge} text-[10px]`}>{(d.status || '—').toUpperCase()}</Badge>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{d.meaning || <span className="italic text-slate-400">no meaning provided</span>}</td>
                          <td className="px-2 text-slate-400">{isOpen ? '−' : '+'}</td>
                        </tr>
                        {isOpen && (
                          <tr className={`${t.row} border-t`}>
                            <td colSpan={5} className="px-3 py-3">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                <CarpmDtcList title="Causes"   color="amber"   items={d.causes} />
                                <CarpmDtcList title="Solutions" color="indigo" items={d.solutions} />
                                <CarpmDtcList title="Symptoms"  color="rose"   items={d.symptoms} />
                              </div>
                              {d.descriptions && d.descriptions.length > 0 && (
                                <div className="mt-3 text-xs text-slate-600">
                                  <span className="font-semibold">Description:</span> {d.descriptions.join(' · ')}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CarpmDtcList({ title, color, items }) {
  const arr = Array.isArray(items) ? items.filter(Boolean) : [];
  const tones = {
    amber:  'border-amber-200 text-amber-800 bg-amber-50',
    indigo: 'border-indigo-200 text-indigo-800 bg-indigo-50',
    rose:   'border-rose-200 text-rose-800 bg-rose-50',
  };
  return (
    <div className={`border rounded-md p-2 ${tones[color] || ''}`}>
      <div className="text-[11px] uppercase font-semibold mb-1">{title}</div>
      {arr.length === 0 ? (
        <div className="text-[11px] italic opacity-60">none</div>
      ) : (
        <ul className="list-disc list-inside space-y-0.5">
          {arr.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
    </div>
  );
}


// ── CARPM webhook panel (Settings → CARPM OBD tab) ──────────────────
// Read-only display of the partner-public POST endpoint CARPM should hit.
// No auth, no rate-limit (per current product decision). The URL is fixed
// to our deployment's REACT_APP_BACKEND_URL.
function CarpmWebhookPanel() {
  const fullUrl = `${API_URL}/b2b/carpm/obd-data`;
  const sampleCurl = `curl -X POST '${fullUrl}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"license_plate":"M1000060", "vin":"...", "code_details":[...]}'`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Clipboard not available');
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        Public endpoint that <span className="font-semibold text-slate-700">CARPM</span> POSTs OBD2 scan JSON to.
        The scan is matched to an MFCWL inspection by mapping the
        <span className="font-mono bg-slate-100 px-1 rounded mx-1">license_plate</span>
        field → our <span className="font-mono bg-slate-100 px-1 rounded mx-1">wisedrive_id</span>
        (the M-prefixed WDS Tracking ID we send the mechanic).
        No authentication required.
      </div>

      <div>
        <Label className="text-xs">Endpoint URL</Label>
        <div className="flex gap-2 mt-1">
          <Input
            readOnly
            value={fullUrl}
            className="font-mono text-xs"
            data-testid="carpm-webhook-url"
          />
          <Button size="sm" variant="outline" onClick={() => copy(fullUrl, 'URL')}
                  data-testid="carpm-webhook-copy">
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy
          </Button>
        </div>
      </div>

      <div>
        <Label className="text-xs">Method &amp; Headers</Label>
        <div className="mt-1 grid grid-cols-2 gap-2 text-xs font-mono">
          <div className="border rounded-md p-2 bg-slate-50">
            <div className="text-[10px] uppercase text-slate-400">Method</div>
            <div className="text-emerald-700 font-semibold">POST</div>
          </div>
          <div className="border rounded-md p-2 bg-slate-50">
            <div className="text-[10px] uppercase text-slate-400">Content-Type</div>
            <div className="text-slate-700">application/json</div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs">Sample cURL</Label>
          <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                  onClick={() => copy(sampleCurl, 'cURL')}
                  data-testid="carpm-curl-copy">
            <Copy className="h-3 w-3 mr-1" /> Copy
          </Button>
        </div>
        <pre className="mt-1 text-[11px] font-mono bg-slate-900 text-slate-100 p-3 rounded-md overflow-auto max-h-[180px]"
             data-testid="carpm-curl-sample">{sampleCurl}</pre>
      </div>

      <div className="border rounded-md p-3 bg-amber-50/40">
        <div className="text-[11px] uppercase font-semibold text-amber-800 mb-1">Required JSON fields</div>
        <ul className="text-xs text-slate-700 space-y-0.5 list-disc list-inside">
          <li><span className="font-mono">license_plate</span> — must equal our <span className="font-mono">wisedrive_id</span> (e.g., <span className="font-mono">M1000060</span>)</li>
          <li>Everything else (<span className="font-mono">vin</span>, <span className="font-mono">code_details[]</span>, <span className="font-mono">faulty_modules[]</span>, …) is stored verbatim and rendered on the OBD2 tab.</li>
        </ul>
      </div>

      <div className="border rounded-md p-3 bg-emerald-50/40">
        <div className="text-[11px] uppercase font-semibold text-emerald-800 mb-1">Response</div>
        <div className="text-xs text-slate-700">
          <span className="font-mono">{'{'}</span>
          <span className="ml-2 font-mono">"ok": true,</span>
          <span className="ml-2 font-mono">"license_plate": "M1000060",</span>
          <span className="ml-2 font-mono">"match_status": "matched" | "unmatched",</span>
          <span className="ml-2 font-mono">"matched_inspection_id": "MFCWL-..."</span>
          <span className="ml-2 font-mono">{'}'}</span>
          <div className="text-[11px] text-slate-500 mt-1">CARPM should treat <span className="font-mono">match_status="unmatched"</span> as a wrong/mistyped license plate.</div>
        </div>
      </div>
    </div>
  );
}

